import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { createPost, getAdConfig, notifyWorkoutStarted, workoutHeartbeat, stopWorkoutHeartbeat, getActiveWorkoutPeers, sendWalkieTalkie, ackWalkieTalkie, sendLiveMedia, getLiveMediaByOwner, ackLiveMedia } from '../lib/api';
import { WalkAdBanner } from '../components/AdBanner';
import { vibrateMedium, vibrateStrong, vibrateMilestone, vibrateHoldStart, vibrateStop } from '../utils/vibration';
import { trackWalkStarted, trackWalkCompleted, trackWalkPaused, trackWalkResumed } from '../lib/analytics';
import { keepAwake, geolocation, isNative } from '../lib/native';
import LocationInfoModal, {
  hasSeenLocationInfo,
  markLocationInfoSeen,
  isAndroid,
} from '../components/LocationInfoModal';
import { hapticMedium, hapticSuccess } from '../lib/haptics';
import { Capacitor, registerPlugin } from '@capacitor/core';
import {
  isWalkieRecorderAvailable,
  showWalkieButton,
  hideWalkieButton,
  requestWalkiePermissions,
  onRecordingStarted as onNativeWalkieStarted,
  onRecordingStopped as onNativeWalkieStopped,
  onRecordingCancelled as onNativeWalkieCancelled,
  onRecordingError as onNativeWalkieError,
} from '../lib/walkie-recorder-native';
import { inferActivityType } from '../lib/activity';
import SharePostModal from '../components/SharePostModal';
import PaceIndicator from '../components/PaceIndicator';
// WalkMiniPlayer kept for future internal-audio feature; not rendered now.
import NoSleep from 'nosleep.js';
import { 
  ChevronLeft, Play, Pause, Square, MapPin, Clock,
  Flame, Navigation, Share2, Check, Save, Satellite, Users, Mic, X, RefreshCw,
  Camera, Image as ImageIcon, Video as VideoIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';
import { useLiveWalkersCount } from '../lib/queries';
import {
  buildTileLayer,
  useMapResumeReload,
} from '../lib/mapUtils';

// ─────────────────────────────────────────────────────────────────────
// Background GPS plugin handle — MUST mirror GpsBackgroundDiagnostic.js
// exactly. We get a fresh handle each call via registerPlugin so the
// plugin lifecycle is identical to the diagnostic that the user has
// already verified works with screen off / app backgrounded.
//
// Returns null on web/PWA or when the plugin isn't registered natively.
// ─────────────────────────────────────────────────────────────────────
const getBgGeoPlugin = () => {
  try {
    if (!Capacitor.isNativePlatform()) return null;
    if (!Capacitor.isPluginAvailable('BackgroundGeolocation')) return null;
    return registerPlugin('BackgroundGeolocation');
  } catch (_) {
    return null;
  }
};

export default function Camminata() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { convertDistance, distanceUnit } = useUnits();
  const [status, setStatus] = useState('idle'); // idle, tracking, paused, finished
  const [positions, setPositions] = useState([]);
  const [currentSpeedKmh, setCurrentSpeedKmh] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [distance, setDistance] = useState(0);
  const [calories, setCalories] = useState(0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [showWalkAd, setShowWalkAd] = useState(false);
  const [adConfig, setAdConfig] = useState({ walk_banner_duration: 5 });
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [satelliteCount, setSatelliteCount] = useState(0);
  const [activePeers, setActivePeers] = useState([]);
  const [walkieModalOpen, setWalkieModalOpen] = useState(false);
  const [walkieRecording, setWalkieRecording] = useState(false);
  const [walkieCountdown, setWalkieCountdown] = useState(10);
  const [walkieSending, setWalkieSending] = useState(false);
  const [walkieError, setWalkieError] = useState('');
  const [walkieIncoming, setWalkieIncoming] = useState(null); // {from_user_name, from_user_picture}
  // Walkie-Talkie feature gate — admin can globally disable the
  // walkie-talkie for regular users from Admin → Settings. Admins
  // always keep access. Defaults to true (backward compat) until
  // /settings/public resolves.
  const [walkieFeatureEnabled, setWalkieFeatureEnabled] = useState(true);
  const walkieAllowed = walkieFeatureEnabled || !!user?.is_admin;

  // One-time informational modal (Android only): explains that precise
  // location is used exclusively during an active workout via the
  // Android foreground location service. NOT a Prominent Disclosure —
  // the app no longer requests ACCESS_BACKGROUND_LOCATION.
  const [locationInfoOpen, setLocationInfoOpen] = useState(false);
  // Debug log retained as a no-op so call sites stay valid. The
  // visible on-screen panel was removed once the Android walkie bug
  // was resolved. To re-enable, replace the body with a setState push.
  const walkieDebug = () => {};
  // Refs to the walkie buttons so we can attach NON-PASSIVE touchstart
  // listeners that call preventDefault. This is the ONLY reliable way
  // to keep ownership of the touch on Chromium WebView — without this,
  // the WebView's scroll-detection heuristic fires `touchcancel`
  // ~100-150ms after touchstart, killing the gesture before the user
  // ever releases. React's onTouchStart is attached as { passive: true }
  // so e.preventDefault() inside it is silently ignored.
  const walkieBtnRef = useRef(null);
  const walkieBtnPausedRef = useRef(null);
  const walkieMediaRef = useRef(null);
  const walkieCountdownIntRef = useRef(null);

  // Live media (photo / short video) shared during the workout —
  // see /workouts/live-media. Three orthogonal pieces of state:
  //   1. `liveMediaOptionsOpen`  → "share" bottom sheet (photo vs video pick)
  //   2. `liveMediaUploading`    → full-screen "uploading" overlay
  //   3. `liveMediaViewer`       → full-screen viewer { items[], index, ownerName, ownerPicture }
  const [liveMediaOptionsOpen, setLiveMediaOptionsOpen] = useState(false);
  const [liveMediaUploading, setLiveMediaUploading] = useState(false);
  const [liveMediaError, setLiveMediaError] = useState('');
  const [liveMediaViewer, setLiveMediaViewer] = useState(null);
  const liveMediaPhotoInputRef = useRef(null);
  const liveMediaVideoInputRef = useRef(null);
  const walkiePlayedIdsRef = useRef(new Set());
  // Unified playback queue. Every incoming walkie message — whether the
  // user is currently recording or not — gets pushed here. A single
  // background worker (`startWalkieWorker`) drains this queue strictly
  // one message at a time with a 1 s silence gap between each, so:
  //   • no two peer audios ever overlap (even across poll ticks)
  //   • recording pauses the worker, releasing resumes it after 1 s
  //   • back-to-back peer bursts feel like a real walkie-talkie channel
  // rather than a chaotic group-call crosstalk.
  const walkieQueueRef = useRef([]);
  const walkieWorkerBusyRef = useRef(false);
  // Persistent mic MediaStream — acquired on the FIRST push-to-talk and
  // reused for every subsequent recording. This avoids the native
  // "Allow microphone?" prompt firing on every press in PWA contexts
  // (iOS in particular re-prompts per call when the stream is stopped).
  // Tracks stay `enabled = false` between recordings so the iOS "red
  // recording bar" and power drain are minimal, then flipped on only
  // while `MediaRecorder` is running.
  const persistentAudioStreamRef = useRef(null);
  // Mirrors the `walkieRecording` state so the polling tick (which has a
  // stale closure) can always read the live value. While true, the
  // playback worker exits at the next iteration and incoming messages
  // keep accumulating in `walkieQueueRef` — they replay once recording
  // ends (after a 1 s "release pause" that mimics real walkie-talkies).
  const walkieRecordingRef = useRef(false);
  // Set once we've successfully acquired the mic at least once during
  // this Camminata session. Guards the iOS "double-tap for permission"
  // branch below: after the first grant we HARD STOP the stream and
  // null the ref (WebKit muted-after-enable workaround), so we can't
  // just rely on `!persistentAudioStreamRef.current` to detect "never
  // asked before" — otherwise the 2nd press would re-enter the prompt
  // branch and the user would need a 3rd press to actually record.
  const walkiePermissionGrantedRef = useRef(false);

  // Keep the ref in sync with the state so callbacks (polling tick,
  // useCallback closures) always see the latest value.
  useEffect(() => {
    walkieRecordingRef.current = walkieRecording;
    if (!walkieRecording) {
      // Recording just ended — wait 1 s (real-walkie "release" pause),
      // then kick the worker so it plays whatever piled up during the
      // recording. If nothing is queued the worker exits immediately.
      const timer = setTimeout(() => {
        startWalkieWorker();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkieRecording]);
  // iOS requires an <audio> element in the DOM that was primed by a real user
  // gesture — we unlock both on the "Start tracking" tap below.
  const walkieAudioElRef = useRef(null);
  const walkieAudioCtxRef = useRef(null);
  const walkieAudioUnlockedRef = useRef(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [holdCountdown, setHoldCountdown] = useState(3);
  const [showHoldHint, setShowHoldHint] = useState(false);
  const holdTimerRef = useRef(null);
  const holdStartRef = useRef(null);
  const holdVibrationStopRef = useRef(null);
  const holdHintTimeoutRef = useRef(null);
  
  // Pause hold states
  const [pauseHoldProgress, setPauseHoldProgress] = useState(0);
  const [isPauseHolding, setIsPauseHolding] = useState(false);
  const [pauseHoldCountdown, setPauseHoldCountdown] = useState(3);
  const [showPauseHoldHint, setShowPauseHoldHint] = useState(false);
  const pauseHoldTimerRef = useRef(null);
  const pauseHoldStartRef = useRef(null);
  const pauseHoldVibrationStopRef = useRef(null);
  const pauseHoldHintTimeoutRef = useRef(null);
  
  // Live walkers state
  const [liveWalkers, setLiveWalkers] = useState({ enabled: false, count: 0 });
  const liveWalkersPingRef = useRef(null);

  // Live walkers count — React Query auto-polls every 10s and pauses in background.
  const { data: liveWalkersData } = useLiveWalkersCount();
  useEffect(() => {
    if (liveWalkersData) setLiveWalkers(liveWalkersData);
  }, [liveWalkersData]);

  // Fetch admin's walkie-talkie feature toggle. Non-admin users lose
  // access when this flips OFF in Admin → Settings. Cached in memory
  // for the session; page reload re-fetches.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${apiBase}/api/settings/public`);
        if (!r.ok) return;
        const cfg = await r.json();
        if (!cancelled) {
          setWalkieFeatureEnabled(cfg.walkie_talkie_enabled !== false);
        }
      } catch (e) { /* keep default true on network failure */ }
    })();
    return () => { cancelled = true; };
  }, []);
  
  const watchIdRef = useRef(null);
  // Background GPS plugin handle — captured at start so pause/stop/cleanup
  // can always stop the EXACT same instance even if registerPlugin returns
  // a new wrapper on a subsequent call.
  const bgPluginRef = useRef(null);
  // Timestamp of the most recent attachGeoWatch() call. We use it to swallow
  // the transient "still acquiring fix" error that iOS @capgo emits in the
  // first ~1-3 seconds after start, which would otherwise flash the red
  // "GPS non disponibile" banner before the first real GPS fix arrives.
  const trackingStartedAtRef = useRef(0);
  const timerRef = useRef(null);
  const noSleepRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const polylineRef = useRef(null);
  const markerRef = useRef(null);
  const leafletLoadedRef = useRef(false);
  const lastKmAlertRef = useRef(0);

  // Vibrate and play sound at each km milestone
  const triggerKmAlert = useCallback((currentKm) => {
    const kmMilestone = Math.floor(currentKm);
    if (kmMilestone > lastKmAlertRef.current && kmMilestone > 0) {
      lastKmAlertRef.current = kmMilestone;
      
      // Strong milestone vibration
      vibrateMilestone();
      
      // Play audio alert
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 880; // A5 note
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        
        oscillator.start();
        setTimeout(() => {
          oscillator.stop();
          audioContext.close();
        }, 300);
      } catch (e) {
        console.log('Audio not supported');
      }
      
      console.log(`🎉 ${kmMilestone} km raggiunto!`);
    }
  }, []);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Calculate calories with pace-aware estimation (covers walking + running).
  // Formula: cal/km = 40 + paceKmh * 5 → 60 cal/km @ 4km/h (walk), 70 @ 6km/h
  // (brisk walk), 80 @ 8km/h (jog), 95 @ 11km/h (run). Matches METs for a ~70kg user.
  const calculateCalories = useCallback((distanceKm, elapsedSec) => {
    if (distanceKm <= 0) return 0;
    const hours = (elapsedSec || 0) / 3600;
    const paceKmh = hours > 0 ? distanceKm / hours : 4; // default to slow walk if no time
    const clampedPace = Math.max(2, Math.min(paceKmh, 14));
    const calPerKm = 40 + clampedPace * 5;
    return Math.round(distanceKm * calPerKm);
  }, []);

  // Initialize map
  useEffect(() => {
    window.scrollTo(0, 0);
    if (leafletLoadedRef.current) return;
    leafletLoadedRef.current = true;

    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const linkEl = document.createElement('link');
      linkEl.rel = 'stylesheet';
      linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      linkEl.crossOrigin = '';
      document.head.appendChild(linkEl);
    }

    // Load Leaflet JS
    const initMap = () => {
      if (mapRef.current && !mapInstanceRef.current && window.L) {
        try {
          // Default to Rome center
          mapInstanceRef.current = window.L.map(mapRef.current, {
            center: [41.9028, 12.4964],
            zoom: 15,
            zoomControl: false
          });
          
          // CartoDB Voyager No Labels — strade, parchi e acqua visibili con
          // colori, SENZA nomi vie o POI (richiesta utente: mappa pulita durante
          // camminata/corsa). Stessa CDN già in uso per produzione stabile.
          tileLayerRef.current = buildTileLayer().addTo(mapInstanceRef.current);

          setMapReady(true);

          // Initial map centering — on native we skip this and let the
          // BackgroundGeolocation watcher's first fix center the map
          // when "Inizia movimento" is pressed. This matches the
          // Diagnostica flow exactly: one permission request, one
          // location source. Calling @capacitor/geolocation here would
          // make iOS list a second "App - Posizione" entry in Privacy
          // (because it's a different CLLocationManager instance).
          if (!isNative && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.setView([pos.coords.latitude, pos.coords.longitude], 16);
                }
              },
              () => {},
              { enableHighAccuracy: true, timeout: 5000 }
            );
          }
        } catch (err) {
          console.error('Map init error:', err);
          setError(t('walk.errors.mapInit'));
        }
      }
    };

    if (window.L) {
      setTimeout(initMap, 100);
    } else {
      const scriptEl = document.createElement('script');
      scriptEl.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      scriptEl.crossOrigin = '';
      scriptEl.onload = () => {
        setTimeout(initMap, 100);
      };
      scriptEl.onerror = () => {
        setError(t('walk.errors.mapLoad'));
      };
      document.body.appendChild(scriptEl);
    }

    return () => {
      const w = watchIdRef.current;
      if (w) {
        try {
          if (w.type === 'bg') {
            // Stop via the captured plugin handle from startTracking.
            // Fallback to a fresh registerPlugin call if the ref is null
            // (e.g. component unmounted before start completed).
            const p = bgPluginRef.current || getBgGeoPlugin();
            if (p) p.stop().catch(() => { /* expected */ });
            bgPluginRef.current = null;
          }
          else if (w.type === 'fg') geolocation.clearWatch(w.id);
          else if (w.type === 'web') navigator.geolocation.clearWatch(w.id);
        } catch (_) { /* ignore */ }
        watchIdRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Cleanup marker when component unmounts
      if (markerRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeLayer(markerRef.current);
          markerRef.current = null;
        } catch (e) {
          // Ignore if map already destroyed
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visibility-change flush REMOVED. The diagnostic in Profilo→Diagnostica
  // proves that @capgo/background-geolocation delivers callbacks reliably
  // even with screen off / app backgrounded, so the previous buffer +
  // pageshow/visibilitychange dance was both unnecessary AND a source of
  // bugs (duplicate timestamps, stale-buffer overwrites, etc.). We now
  // mirror the diagnostic 1:1: every fix calls setPositions directly.

  // ─────────────────────────────────────────────────────────────────────
  // Responsive map: when the container resizes (different screen heights,
  // pace indicator toggling, peers section growing, etc.) Leaflet needs
  // to be told to recompute tile layout. Otherwise the visible area
  // becomes inconsistent with the available space.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (mapInstanceRef.current) {
          try { mapInstanceRef.current.invalidateSize(); } catch (_) { /* noop */ }
        }
      });
    });
    ro.observe(mapRef.current);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [mapReady]);

  // ─────────────────────────────────────────────────────────────────────
  // Reload map tiles when the app returns to the foreground (iOS/Android
  // WebView background tile purge). NUCLEAR strategy: fully destroy and
  // recreate the Leaflet map. After rebuild we null-out polyline/marker
  // refs and bump `mapRebuildCounter` so the polyline-useEffect below
  // re-draws the route on the fresh instance.
  // ─────────────────────────────────────────────────────────────────────
  const [mapRebuildCounter, setMapRebuildCounter] = useState(0);
  const manualReloadMap = useMapResumeReload({
    mapRef,
    mapInstanceRef,
    tileLayerRef,
    onAfterRebuild: () => {
      // Old polyline/marker belonged to the destroyed map — drop refs.
      polylineRef.current = null;
      markerRef.current = null;
      // Trigger the polyline-useEffect to re-draw everything.
      setMapRebuildCounter((n) => n + 1);
    },
  });



  // Fetch live walkers count — delegated to useLiveWalkersCount hook above
  // (React Query handles polling, dedup, and background-pause automatically)

  // Join/leave live walkers when tracking status changes
  useEffect(() => {
    if (status === 'tracking') {
      // Join live walkers
      axios.post(`${API_URL}/api/walks/live/join`, {}, { withCredentials: true }).catch(() => {});
      
      // Ping every 30 seconds to stay active
      liveWalkersPingRef.current = setInterval(() => {
        axios.post(`${API_URL}/api/walks/live/ping`, {}, { withCredentials: true }).catch(() => {});
      }, 30000);
    } else if (status === 'idle' || status === 'finished') {
      // Leave live walkers
      axios.post(`${API_URL}/api/walks/live/leave`, {}, { withCredentials: true }).catch(() => {});
      
      // Clear ping interval
      if (liveWalkersPingRef.current) {
        clearInterval(liveWalkersPingRef.current);
        liveWalkersPingRef.current = null;
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (liveWalkersPingRef.current) {
        clearInterval(liveWalkersPingRef.current);
      }
      // Leave when component unmounts
      axios.post(`${API_URL}/api/walks/live/leave`, {}, { withCredentials: true }).catch(() => {});
    };
  }, [status]);

  // Update polyline on map
  useEffect(() => {
    if (mapInstanceRef.current && window.L && positions.length > 0 && mapReady) {
      try {
        const coords = positions.map(p => [p.lat, p.lng]);
        
        // Update or create polyline (blue color)
        if (polylineRef.current) {
          polylineRef.current.setLatLngs(coords);
        } else {
          polylineRef.current = window.L.polyline(coords, {
            color: '#3B82F6',
            weight: 5,
            opacity: 0.9
          }).addTo(mapInstanceRef.current);
        }

        // Update or create current position marker (larger blue dot)
        const last = positions[positions.length - 1];
        
        if (markerRef.current) {
          markerRef.current.setLatLng([last.lat, last.lng]);
        } else {
          // Create a custom circle marker (bigger and more visible)
          markerRef.current = window.L.circleMarker([last.lat, last.lng], {
            radius: 12,
            fillColor: '#3B82F6',
            fillOpacity: 1,
            color: '#ffffff',
            weight: 3,
            opacity: 1
          }).addTo(mapInstanceRef.current);
        }
        
        // Always keep current position centered (even after user zoom/pan)
        const currentZoom = mapInstanceRef.current.getZoom();
        mapInstanceRef.current.setView([last.lat, last.lng], currentZoom, { animate: true });
      } catch (err) {
        console.error('Polyline error:', err);
      }
    }
  }, [positions, mapReady, mapRebuildCounter]);

  // Timer - uses real elapsed time (resilient to standby/throttling)
  useEffect(() => {
    if (status === 'tracking') {
      timerRef.current = setInterval(() => {
        if (startTime) {
          // Calculate from real wall-clock time, not increments
          const savedPaused = parseInt(localStorage.getItem('walk_paused_duration') || '0');
          const realElapsed = Math.floor((Date.now() - startTime.getTime()) / 1000) - savedPaused;
          setElapsedTime(realElapsed > 0 ? realElapsed : 0);
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status, startTime]);

  // Heartbeat: send my live session stats + poll peer sessions while tracking
  // OR paused. During pause we keep pinging so other users don't see us
  // disappear from the peers list after 60s (the cutoff on /active-peers).
  useEffect(() => {
    if (status !== 'tracking' && status !== 'paused') {
      // Remove my live session when I stop/finish.
      if (status === 'finished') {
        stopWorkoutHeartbeat().catch(() => {});
      }
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        await workoutHeartbeat({
          distance_km: distance,
          elapsed_sec: elapsedTime,
          type: 'walk',
          started_at: startTime ? startTime.toISOString() : null
        });
      } catch { /* noop */ }
      try {
        const res = await getActiveWorkoutPeers();
        if (!cancelled) {
          setActivePeers(res.data?.peers || []);
          const inbox = res.data?.walkie_inbox || [];
          if (inbox.length > 0) playWalkieInbox(inbox);
        }
      } catch { /* noop */ }
    };

    tick(); // immediate
    const id = setInterval(tick, 10000); // every 10s

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [status, distance, elapsedTime, startTime]);

  // Idle peers polling — show challenge members even before the user starts
  // moving. Lighter cadence (15s) and no heartbeat: we're not broadcasting
  // our own activity, just observing the others.
  useEffect(() => {
    if (status !== 'idle') return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await getActiveWorkoutPeers();
        if (!cancelled) setActivePeers(res.data?.peers || []);
      } catch { /* noop */ }
    };

    tick();
    const id = setInterval(tick, 15000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [status]);

  // Foreground push listener: when a peer shares a live photo/video,
  // FCM delivers a payload with `data.live_media_id`. We trigger an
  // immediate `getActiveWorkoutPeers` refresh so the pulsing border
  // appears within ~1 second instead of waiting for the next 15s tick.
  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;
    (async () => {
      try {
        const { onForegroundMessage } = await import('../lib/firebase');
        unsub = await onForegroundMessage((payload) => {
          if (cancelled) return;
          const data = payload?.data || {};
          if (data.live_media_id || data.notification_type === 'live_media') {
            // Immediate refresh — the new peer will now show with
            // has_live_media=true and the pink pulsing ring.
            getActiveWorkoutPeers()
              .then(res => { if (!cancelled) setActivePeers(res.data?.peers || []); })
              .catch(() => {});
          }
        });
      } catch (_) { /* messaging not available */ }
    })();
    return () => {
      cancelled = true;
      try { unsub(); } catch (_) {}
    };
  }, []);

  // Also cleanup live session on unmount
  useEffect(() => {
    return () => {
      stopWorkoutHeartbeat().catch(() => {});
    };
  }, []);

  // Load ad config on mount
  useEffect(() => {
    getAdConfig().then(res => setAdConfig(res.data)).catch(() => {});
  }, []);

  // Persist walk data to localStorage frequently + on critical browser events
  // (the tab can be killed without visibilitychange firing — especially on iOS PWA)
  useEffect(() => {
    if (status !== 'tracking' && status !== 'paused') return;

    const saveNow = () => {
      try {
        localStorage.setItem('walk_positions', JSON.stringify(positions));
        localStorage.setItem('walk_distance', String(distance));
        localStorage.setItem('walk_calories', String(calories));
        localStorage.setItem('walk_status', status);
        if (startTime) localStorage.setItem('walk_start_time', startTime.toISOString());
      } catch (e) { /* storage full, ignore */ }
    };

    // Periodic save every 5s (was 10s — cuts potential data loss in half)
    const saveInterval = setInterval(saveNow, 5000);

    // Save before the OS/browser kills the tab. pagehide is the most reliable
    // lifecycle event on mobile (iOS Safari fires it on background/kill).
    window.addEventListener('pagehide', saveNow);
    window.addEventListener('beforeunload', saveNow);
    window.addEventListener('blur', saveNow);

    return () => {
      clearInterval(saveInterval);
      window.removeEventListener('pagehide', saveNow);
      window.removeEventListener('beforeunload', saveNow);
      window.removeEventListener('blur', saveNow);
    };
  }, [status, positions, distance, calories, startTime]);

  // On mount: if an interrupted walk is found in localStorage, restore it
  // (handles the case where the PWA/browser was killed entirely).
  useEffect(() => {
    try {
      const savedStatus = localStorage.getItem('walk_status');
      const savedPositions = localStorage.getItem('walk_positions');
      const savedStart = localStorage.getItem('walk_start_time');

      if ((savedStatus === 'tracking' || savedStatus === 'paused') && savedPositions && savedStart) {
        const parsedPositions = JSON.parse(savedPositions);
        const startDate = new Date(savedStart);

        // Guard: only restore if the session is reasonably fresh (< 12h)
        const hoursAgo = (Date.now() - startDate.getTime()) / 3_600_000;
        if (hoursAgo > 12) {
          clearWalkStorage();
          return;
        }

        setPositions(parsedPositions);
        setDistance(parseFloat(localStorage.getItem('walk_distance') || '0'));
        setCalories(parseFloat(localStorage.getItem('walk_calories') || '0'));
        setStartTime(startDate);

        const pausedDuration = parseInt(localStorage.getItem('walk_paused_duration') || '0', 10);
        const realElapsed = Math.floor((Date.now() - startDate.getTime()) / 1000) - pausedDuration;
        if (realElapsed > 0) setElapsedTime(realElapsed);

        // Restore status last so the visibility-restore effect has the right state
        setStatus(savedStatus);
      }
    } catch (e) { /* corrupted storage — ignore */ }
    // Run exactly once on mount — we intentionally omit deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore walk data on visibility change (wake from standby)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && (status === 'tracking' || status === 'paused')) {
        try {
          const savedPositions = localStorage.getItem('walk_positions');
          const savedDistance = localStorage.getItem('walk_distance');
          const savedCalories = localStorage.getItem('walk_calories');
          const savedStart = localStorage.getItem('walk_start_time');
          
          if (savedPositions) {
            const parsed = JSON.parse(savedPositions);
            if (parsed.length > positions.length) {
              setPositions(parsed);
            }
          }
          if (savedDistance) setDistance(parseFloat(savedDistance));
          if (savedCalories) setCalories(parseFloat(savedCalories));
          if (savedStart && !startTime) setStartTime(new Date(savedStart));
          
          // Recalculate elapsed from real clock
          if (savedStart) {
            const pausedDuration = parseInt(localStorage.getItem('walk_paused_duration') || '0');
            const realElapsed = Math.floor((Date.now() - new Date(savedStart).getTime()) / 1000) - pausedDuration;
            if (realElapsed > 0) setElapsedTime(realElapsed);
          }
        } catch (e) { /* parse error, ignore */ }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [status, positions, startTime]);

  // Clean up localStorage when walk finishes or component unmounts idle
  const clearWalkStorage = () => {
    localStorage.removeItem('walk_positions');
    localStorage.removeItem('walk_distance');
    localStorage.removeItem('walk_calories');
    localStorage.removeItem('walk_start_time');
    localStorage.removeItem('walk_status');
    localStorage.removeItem('walk_paused_duration');
    localStorage.removeItem('walk_pause_start');
  };

  // Hide bottom nav while on the Camminata page (full-screen experience).
  // Restore it on unmount.
  useEffect(() => {
    const bottomNav = document.querySelector('[data-testid="bottom-nav"]');
    if (bottomNav) {
      bottomNav.style.display = 'none';
    }
    return () => {
      if (bottomNav) {
        bottomNav.style.display = '';
      }
    };
  }, []);

  // Attach the GPS watcher. Shared between fresh start and auto-resume after
  // the app was killed and the session is restored from localStorage.
  const attachGeoWatch = useCallback(() => {
    if (watchIdRef.current) return; // already watching

    // Mark the exact moment tracking began — handleError uses this to
    // suppress the transient "still acquiring fix" errors emitted by iOS
    // @capgo in the first few seconds before the first real GPS lock.
    trackingStartedAtRef.current = Date.now();

    const handlePosition = (position) => {
      const coords = position.coords || position;
      // First successful fix clears any transient "GPS non disponibile"
      // banner — iOS @capgo can emit early "still acquiring" errors before
      // the first GPS lock, and we don't want the red banner to stay up
      // once we're actually receiving valid fixes.
      setError('');
      const newPos = {
        lat: coords.latitude,
        lng: coords.longitude,
        timestamp: position.timestamp || Date.now()
      };

      // Direct setPositions on every native fix — mirrors the diagnostic
      // exactly. No ref buffer, no visibilitychange flush. iOS keeps the
      // JS callback running in background as long as the @capgo plugin
      // session is alive (verified via GpsBackgroundDiagnostic).

      // Update GPS accuracy and estimate satellite count
      const accuracy = coords.accuracy || 50;
      setGpsAccuracy(accuracy);
      let satellites = 0;
      if (accuracy < 5) satellites = Math.floor(Math.random() * 5) + 8;
      else if (accuracy < 15) satellites = Math.floor(Math.random() * 4) + 5;
      else if (accuracy < 30) satellites = Math.floor(Math.random() * 3) + 3;
      else satellites = Math.floor(Math.random() * 3) + 1;
      setSatelliteCount(satellites);

      setPositions(prev => {
        const newPositions = [...prev, newPos];

        if (newPositions.length > 1) {
          let totalDist = 0;
          for (let i = 1; i < newPositions.length; i++) {
            totalDist += calculateDistance(
              newPositions[i-1].lat, newPositions[i-1].lng,
              newPositions[i].lat, newPositions[i].lng
            );
          }
          setDistance(totalDist);
          // Elapsed seconds from first to latest GPS fix — fresh, no closure staleness
          const elapsedSec = Math.max(0, (newPos.timestamp - newPositions[0].timestamp) / 1000);
          setCalories(calculateCalories(totalDist, elapsedSec));

          // Trigger vibration alert at each km
          triggerKmAlert(totalDist);

          // ─────────────────────────────────────────────────────────────
          // SMOOTHED SPEED CALCULATION
          // ─────────────────────────────────────────────────────────────
          // Goal: a calm, readable indicator that doesn't jitter on every
          // GPS micro-correction, but still tracks the user's actual pace.
          //
          // Pipeline:
          //   1) Resolve a "raw" instant speed for this fix:
          //        a) prefer device-reported `coords.speed` (m/s) when
          //           present and non-null (already Kalman-filtered);
          //        b) otherwise compute over a 5-second sliding window
          //           on the last fixes.
          //   2) If the gap from the previous fix is > 10s (likely a
          //      background pause / incoming call) → skip update.
          //   3) Apply an exponential moving average (EMA) so the UI
          //      converges towards the new value smoothly rather than
          //      snapping to it. alpha = 0.25 → ~3-4 fixes to fully react.
          //
          const prevPos = newPositions[newPositions.length - 2];
          const dtSec = (newPos.timestamp - prevPos.timestamp) / 1000;
          const BACKGROUND_GAP_SEC = 10; // suspected background pause
          const WINDOW_SEC = 5;          // sliding window length
          const EMA_ALPHA = 0.25;        // smoothing factor (lower = smoother)

          let rawKmh = null;
          if (typeof coords.speed === 'number' && coords.speed >= 0 && !isNaN(coords.speed)) {
            rawKmh = coords.speed * 3.6;
          } else if (dtSec > 0 && dtSec <= BACKGROUND_GAP_SEC) {
            // Build a window covering ~WINDOW_SEC of recent history
            let i = newPositions.length - 1;
            let acc = 0;
            while (i > 0) {
              const segDt = (newPositions[i].timestamp - newPositions[i-1].timestamp) / 1000;
              if (acc + segDt > WINDOW_SEC && acc > 0) break;
              acc += segDt;
              i -= 1;
            }
            const windowStart = newPositions[i];
            const windowEnd = newPositions[newPositions.length - 1];
            const windowSec = (windowEnd.timestamp - windowStart.timestamp) / 1000;
            if (windowSec > 0) {
              let windowDist = 0;
              for (let j = i + 1; j < newPositions.length; j++) {
                windowDist += calculateDistance(
                  newPositions[j-1].lat, newPositions[j-1].lng,
                  newPositions[j].lat, newPositions[j].lng
                );
              }
              rawKmh = (windowDist / windowSec) * 3600;
            }
          }

          if (rawKmh !== null && dtSec <= BACKGROUND_GAP_SEC && rawKmh <= 30) {
            // Apply EMA towards the new reading
            setCurrentSpeedKmh(prevKmh => {
              // Seed the EMA on the first valid sample so we don't crawl
              // up from zero for the first ~15s of the walk.
              if (prevKmh <= 0) return rawKmh;
              return prevKmh + EMA_ALPHA * (rawKmh - prevKmh);
            });
          }
          // else: keep previous speed (background gap or implausible spike)
        }

        return newPositions;
      });
    };

    const handleError = (err) => {
      console.error('GPS error:', err);
      // Permission-denied (code 1) is always a real, immediate error — show it.
      const isPermissionError = err?.code === 1;
      // For any other error, suppress it during the first 3 seconds after
      // tracking started: iOS @capgo / WKWebView routinely emit one or two
      // transient "kCLErrorLocationUnknown" / "still acquiring fix" errors
      // before the first GPS lock, and we don't want the red banner to
      // flash on screen and disappear a second later.
      if (!isPermissionError) {
        const elapsed = Date.now() - trackingStartedAtRef.current;
        if (trackingStartedAtRef.current > 0 && elapsed < 3000) {
          return; // swallow transient acquisition error
        }
      }
      let errorMessage = t('walk.errors.gpsUnavailable');
      if (err?.code === 1) {
        errorMessage = t('walk.errors.gpsPermission');
      } else if (err?.code === 2 || err?.message?.includes('kCLErrorDomain')) {
        errorMessage = t('walk.errors.gpsUnavailable');
      } else if (err?.code === 3) {
        errorMessage = t('walk.errors.gpsTimeout');
      }
      setError(errorMessage);
    };

    // Native (iOS/Android Capacitor): use EXCLUSIVELY @capgo/background-geolocation
    // via inline registerPlugin — IDENTICAL pattern to GpsBackgroundDiagnostic.js
    // which the user has verified delivers the "Consenti sempre" upgrade prompt.
    //
    // iOS PERMISSION DANCE (CRITICAL):
    //   plugin.start({requestPermissions: true}) calls CLLocationManager
    //   .requestAlwaysAuthorization() under the hood. iOS behavior:
    //     - 1st call when permission is "Not Determined" → shows "When in Use" prompt
    //     - 2nd call when permission is "When in Use"   → shows "Always" upgrade prompt
    //   So a SINGLE plugin.start call from cold-start only gets WhenInUse.
    //   The Diagnostica gets the upgrade prompt because the user first tapped
    //   "Richiedi permessi" (which calls Geolocation.requestPermissions →
    //   triggers iOS to grant WhenInUse), THEN tapped "Avvia test live"
    //   (second call → triggers Always upgrade).
    //
    //   We replicate that two-step dance HERE, sequentially awaited, so the
    //   user gets WhenInUse first, then Always upgrade, on a single tap.
    //
    // ANDROID: the manifest does NOT declare ACCESS_BACKGROUND_LOCATION,
    //   so requestPermissions only surfaces FINE/COARSE ("Durante l'uso
    //   dell'app"). Tracking with screen off / app backgrounded is kept
    //   alive by the plugin's Foreground Service (type "location") with
    //   its persistent notification — no "Consenti sempre" is ever
    //   requested. The service stops with plugin.stop() on workout end.
    if (isNative) {
      (async () => {
        // STEP 1 — Trigger the "Quando in uso" prompt and AWAIT user choice.
        // This is what enables iOS to show the "Sempre" upgrade prompt on
        // the next requestAuthorization call (= plugin.start below).
        try {
          const { Geolocation } = await import('@capacitor/geolocation');
          await Geolocation.requestPermissions({ permissions: ['location'] });
        } catch (_) { /* user denied / plugin missing — start() will still try */ }

        const plugin = getBgGeoPlugin();
        if (!plugin) {
          handleError({ code: 2, message: 'BackgroundGeolocation plugin not available' });
          return;
        }
        bgPluginRef.current = plugin;

        // STEP 2 — iOS: now that WhenInUse is granted, requestPermissions:true
        // triggers the "Passa a Consenti sempre" upgrade prompt (iOS-only).
        // Android: no background permission exists in the manifest — this
        // simply starts the Foreground Service with its notification.
        try {
          plugin.start(
            {
              backgroundTitle: t('walk.bgTitle') || 'Slimmy',
              backgroundMessage: t('walk.bgMessage') || 'Tracciamento del movimento in corso',
              requestPermissions: true,
              stale: false,
              distanceFilter: 0,
            },
            (location, err) => {
              if (err) {
                handleError(err);
                return;
              }
              if (!location) return;
              // @capgo schema → navigator.geolocation-style position
              handlePosition({
                coords: {
                  latitude: location.latitude,
                  longitude: location.longitude,
                  accuracy: location.accuracy,
                  altitude: location.altitude ?? null,
                  altitudeAccuracy: location.altitudeAccuracy ?? null,
                  heading: location.bearing ?? null,
                  speed: location.speed ?? null,
                },
                timestamp: location.time || Date.now(),
              });
            }
          ).catch(() => { /* expected on stop / unavailable */ });
          watchIdRef.current = { type: 'bg' };
        } catch (e) {
          handleError(e);
        }
      })();
    } else {
      // Web / PWA: navigator.geolocation is fine (single foreground stream).
      const id = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 }
      );
      watchIdRef.current = { type: 'web', id };
    }
  }, [calculateDistance, calculateCalories, triggerKmAlert, t]);

  // Auto-resume: if the component just restored a 'tracking' session from
  // localStorage (app was killed), re-attach the GPS watcher automatically.
  useEffect(() => {
    if (status === 'tracking' && !watchIdRef.current) {
      // Re-enable keep-awake + NoSleep (they were killed with the previous tab)
      try { keepAwake.enable(); } catch (e) { /* ignore */ }
      if (!isNative) {
        try {
          if (!noSleepRef.current) noSleepRef.current = new NoSleep();
          noSleepRef.current.enable();
        } catch (e) { /* ignore — needs user gesture, retried on next tap */ }
      }
      attachGeoWatch();
    }
  }, [status, attachGeoWatch]);

  // One-time (Android-only) informational modal about location usage
  // during workouts. Purely informative — after the first acceptance
  // the workout always starts directly.
  const requestStartTracking = useCallback(() => {
    if (isAndroid() && !hasSeenLocationInfo()) {
      setLocationInfoOpen(true);
      return;
    }
    startTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationInfoAccept = useCallback(() => {
    markLocationInfoSeen();
    setLocationInfoOpen(false);
    startTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationInfoDecline = useCallback(() => {
    setLocationInfoOpen(false);
  }, []);

  const startTracking = async () => {
    setError('');
    hapticMedium();
    lastKmAlertRef.current = 0;

    // Geolocation permission warm-up — REQUIRED for Android WebView wrappers
    // (Natively/TWA). Without this, watchPosition() can fail silently with
    // PERMISSION_DENIED because the system prompt is never shown. We trigger
    // the request from inside the user-tap handler so the WebView host can
    // surface the Android permission dialog.
    //
    // On Capacitor native we let the Capacitor geolocation plugin handle
    // permissions itself (don't double-prompt).
    if (!isNative && typeof navigator !== 'undefined' && navigator.geolocation) {
      try {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(),
            (err) => {
              // PERMISSION_DENIED → surface a clear error to the user
              if (err && err.code === 1) {
                setError(t('walk.errors.gpsPermission'));
              }
              resolve(); // never block: attachGeoWatch will retry / show error
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
          );
        });
      } catch (_) {
        // Best-effort warm-up — proceed regardless
      }
    }

    // iOS audio unlock — MUST happen inside the tap handler so Safari counts
    // this as a "user gesture" and allows subsequent background playback.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !walkieAudioCtxRef.current) {
        walkieAudioCtxRef.current = new Ctx();
      }
      if (walkieAudioCtxRef.current && walkieAudioCtxRef.current.state === 'suspended') {
        walkieAudioCtxRef.current.resume().catch(() => {});
      }
      const el = walkieAudioElRef.current;
      if (el && !walkieAudioUnlockedRef.current) {
        // Tiny silent WAV (1 sample) to prime the element
        el.src = 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQIAAAAAAA==';
        el.play().then(() => {
          el.pause();
          el.currentTime = 0;
          walkieAudioUnlockedRef.current = true;
        }).catch(() => { /* non fatal */ });
      }
    } catch { /* noop */ }

    // Track walk started in analytics
    trackWalkStarted();

    // Notify challenge members (fire-and-forget)
    notifyWorkoutStarted('walk').catch(err => console.warn('Workout notify failed:', err));
    
    // Vibrate on start
    vibrateMedium();

    // Keep screen on during walk
    keepAwake.enable();
    
    // On web: use NoSleep.js to prevent browser standby
    if (!isNative) {
      try {
        if (!noSleepRef.current) {
          noSleepRef.current = new NoSleep();
        }
        noSleepRef.current.enable();
      } catch (e) { console.warn('NoSleep failed:', e); }
    }
    
    // Show ad banner when starting walk (only for non-premium users)
    const isPremium = user?.is_premium || (user?.premium_expires_at && new Date(user.premium_expires_at) > new Date());
    if (!isPremium) {
      setShowWalkAd(true);
    }
    
    if (!navigator.geolocation && !isNative) {
      setError(t('walk.errors.gpsNotSupported'));
      return;
    }

    // Only reset startTime for a fresh start, not when resuming from pause
    // (resumeTracking also calls startTracking but startTime should stay)
    setStatus('tracking');
    setStartTime(prev => prev || new Date());

    attachGeoWatch();
  };

  const pauseTracking = () => {
    // Track pause in analytics
    trackWalkPaused();
    // Vibrate on pause
    vibrateMedium();
    
    // Save pause start time to calculate paused duration
    localStorage.setItem('walk_pause_start', String(Date.now()));
    
    setStatus('paused');
    setCurrentSpeedKmh(0);
    const w = watchIdRef.current;
    if (w) {
      try {
        if (w.type === 'bg') {
          const p = bgPluginRef.current || getBgGeoPlugin();
          if (p) p.stop().catch(() => { /* expected */ });
          bgPluginRef.current = null;
        }
        else if (w.type === 'fg') geolocation.clearWatch(w.id);
        else if (w.type === 'web') navigator.geolocation.clearWatch(w.id);
      } catch (_) { /* ignore */ }
      watchIdRef.current = null;
    }
  };

  const resumeTracking = () => {
    // Accumulate paused duration
    const pauseStart = parseInt(localStorage.getItem('walk_pause_start') || '0');
    if (pauseStart > 0) {
      const pausedSecs = Math.floor((Date.now() - pauseStart) / 1000);
      const prevPaused = parseInt(localStorage.getItem('walk_paused_duration') || '0');
      localStorage.setItem('walk_paused_duration', String(prevPaused + pausedSecs));
      localStorage.removeItem('walk_pause_start');
    }
    // Track resume in analytics
    trackWalkResumed();
    startTracking();
  };

  const stopTracking = () => {
    // Track walk completed in analytics
    const durationMinutes = Math.round(elapsedTime / 60);
    trackWalkCompleted(distance, durationMinutes, calories);

    // Allow screen to sleep again
    keepAwake.disable();
    
    // Disable NoSleep on web
    if (noSleepRef.current) {
      try { noSleepRef.current.disable(); } catch (e) { /* ignore */ }
    }
    
    // Clean up localStorage persistence
    clearWalkStorage();
    
    setStatus('finished');
    hapticSuccess();
    setCurrentSpeedKmh(0);
    const w = watchIdRef.current;
    if (w) {
      try {
        if (w.type === 'bg') {
          const p = bgPluginRef.current || getBgGeoPlugin();
          if (p) p.stop().catch(() => { /* expected */ });
          bgPluginRef.current = null;
        }
        else if (w.type === 'fg') geolocation.clearWatch(w.id);
        else if (w.type === 'web') navigator.geolocation.clearWatch(w.id);
      } catch (_) { /* ignore */ }
      watchIdRef.current = null;
    }
    // Remove the current position marker when tracking stops
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    setShowPublishModal(true);
  };

  // Hold-to-stop functionality
  const HOLD_DURATION = 3000; // 3 seconds
  
  const startHold = () => {
    setIsHolding(true);
    setHoldProgress(0);
    setHoldCountdown(3);
    holdStartRef.current = Date.now();
    
    // Start continuous light vibration while holding
    holdVibrationStopRef.current = vibrateHoldStart();
    
    // Update progress every 30ms for smooth animation
    holdTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - holdStartRef.current;
      const progress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setHoldProgress(progress);
      
      // Calculate countdown (3, 2, 1, 0)
      const remaining = Math.ceil((HOLD_DURATION - elapsed) / 1000);
      setHoldCountdown(Math.max(0, remaining));
      
      if (progress >= 100) {
        // Hold completed - stop the walk
        clearInterval(holdTimerRef.current);
        // Stop the continuous vibration
        if (holdVibrationStopRef.current) {
          holdVibrationStopRef.current();
        }
        // Strong vibration to confirm stop
        vibrateStrong();
        setIsHolding(false);
        setHoldProgress(0);
        setHoldCountdown(3);
        stopTracking();
      }
    }, 30);
  };
  
  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
    }
    // Stop vibration when releasing early
    if (holdVibrationStopRef.current) {
      holdVibrationStopRef.current();
    }
    vibrateStop();
    setIsHolding(false);
    setHoldProgress(0);
    setHoldCountdown(3);
    
    // Show the "Hold to end" hint badge for 2 seconds
    // Clear any existing timeout first
    if (holdHintTimeoutRef.current) {
      clearTimeout(holdHintTimeoutRef.current);
    }
    setShowHoldHint(true);
    holdHintTimeoutRef.current = setTimeout(() => {
      setShowHoldHint(false);
    }, 2000);
  };

  // Pause hold functions (same logic as stop, but 2 seconds and calls pauseTracking)
  const PAUSE_HOLD_DURATION = 2000; // 2 seconds for pause
  
  const startPauseHold = () => {
    setIsPauseHolding(true);
    setPauseHoldProgress(0);
    setPauseHoldCountdown(2);
    pauseHoldStartRef.current = Date.now();
    
    // Start continuous light vibration while holding
    pauseHoldVibrationStopRef.current = vibrateHoldStart();
    
    // Update progress every 30ms for smooth animation
    pauseHoldTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - pauseHoldStartRef.current;
      const progress = Math.min((elapsed / PAUSE_HOLD_DURATION) * 100, 100);
      setPauseHoldProgress(progress);
      
      // Calculate countdown (2, 1, 0)
      const remaining = Math.ceil((PAUSE_HOLD_DURATION - elapsed) / 1000);
      setPauseHoldCountdown(Math.max(0, remaining));
      
      if (progress >= 100) {
        // Hold completed - pause the walk
        clearInterval(pauseHoldTimerRef.current);
        // Stop the continuous vibration
        if (pauseHoldVibrationStopRef.current) {
          pauseHoldVibrationStopRef.current();
        }
        // Strong vibration to confirm pause
        vibrateStrong();
        setIsPauseHolding(false);
        setPauseHoldProgress(0);
        setPauseHoldCountdown(2);
        pauseTracking();
      }
    }, 30);
  };
  
  const cancelPauseHold = () => {
    if (pauseHoldTimerRef.current) {
      clearInterval(pauseHoldTimerRef.current);
    }
    // Stop vibration when releasing early
    if (pauseHoldVibrationStopRef.current) {
      pauseHoldVibrationStopRef.current();
    }
    vibrateStop();
    setIsPauseHolding(false);
    setPauseHoldProgress(0);
    setPauseHoldCountdown(2);
    
    // Show the "Hold to pause" hint badge for 2 seconds
    if (pauseHoldHintTimeoutRef.current) {
      clearTimeout(pauseHoldHintTimeoutRef.current);
    }
    setShowPauseHoldHint(true);
    pauseHoldHintTimeoutRef.current = setTimeout(() => {
      setShowPauseHoldHint(false);
    }, 2000);
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    // Rule: < 1h → MM:SS (fino a 59:59). ≥ 1h → H:MM h (con suffisso "h" per chiarezza).
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')} h`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ─────────────────────────────────────────────────────────────
  // WALKIE-TALKIE — record & broadcast a 5s voice note to active peers
  //
  // We encode to 16-bit PCM WAV @ 16kHz mono ourselves (via Web Audio API)
  // instead of relying on MediaRecorder — this avoids the iOS/Android codec
  // mismatch (iOS Safari cannot decode WebM/Opus, Android Chrome cannot
  // always produce MP4/AAC). WAV plays everywhere.
  // ─────────────────────────────────────────────────────────────
  // Pre-generated 200ms 880Hz beep WAV (base64). Using an <audio> element
  // with a fixed WAV gives us iOS-reliable playback even after long idle:
  // AudioContext gets auto-suspended on iOS, but an HTMLAudioElement
  // primed by the user gesture in startTracking keeps its "can autoplay"
  // flag across src changes.
  const BEEP_WAV_B64 = (() => {
    const sr = 22050, dur = 0.15;
    const n = Math.floor(sr * dur);
    const buf = new ArrayBuffer(44 + n * 2);
    const v = new DataView(buf);
    const w = (p, s) => { for (let i = 0; i < s.length; i++) v.setUint8(p + i, s.charCodeAt(i)); };
    w(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); w(8, 'WAVE');
    w(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
    v.setUint16(22, 1, true); v.setUint32(24, sr, true);
    v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
    w(36, 'data'); v.setUint32(40, n * 2, true);
    let p = 44;
    for (let i = 0; i < n; i++) {
      // 880Hz sine with gentle 5ms attack/decay envelope
      const t = i / sr;
      let env = 1;
      if (t < 0.005) env = t / 0.005;
      else if (t > dur - 0.01) env = (dur - t) / 0.01;
      const s = Math.sin(2 * Math.PI * 880 * t) * 0.4 * Math.max(0, env);
      v.setInt16(p, Math.max(-1, Math.min(1, s)) * 0x7FFF, true);
      p += 2;
    }
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    // btoa is available in the browser
    // eslint-disable-next-line no-undef
    return btoa(bin);
  })();

  const playBeep = async () => {
    const el = walkieAudioElRef.current;
    if (el) {
      try {
        el.src = `data:audio/wav;base64,${BEEP_WAV_B64}`;
        el.volume = 0.6;
        await new Promise((resolve) => {
          const done = () => { el.onended = null; el.onerror = null; resolve(); };
          el.onended = done;
          el.onerror = done;
          el.play().catch(done);
        });
      } catch { /* ignore */ }
      return;
    }
    // Fallback (shouldn't normally hit): AudioContext oscillator
    try {
      const ctx = walkieAudioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.value = 0.25;
      osc.connect(gain); gain.connect(ctx.destination);
      const now = ctx.currentTime;
      osc.start(now);
      osc.stop(now + 0.16);
    } catch { /* ignore */ }
  };

  // Encode Float32 PCM → 16-bit mono WAV @ 16kHz. Used as a last-resort
  // fallback when MediaRecorder isn't available (very rare).
  const encodeWav = (chunks, sourceSampleRate) => {
    let total = 0;
    for (const c of chunks) total += c.length;
    const flat = new Float32Array(total);
    let off = 0;
    for (const c of chunks) { flat.set(c, off); off += c.length; }

    const targetRate = 16000;
    const ratio = sourceSampleRate / targetRate;
    const outLen = Math.max(1, Math.floor(flat.length / ratio));
    const resampled = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const idx = i * ratio;
      const lo = Math.floor(idx);
      const hi = Math.min(flat.length - 1, lo + 1);
      const t = idx - lo;
      resampled[i] = flat[lo] * (1 - t) + flat[hi] * t;
    }
    const dataBytes = resampled.length * 2;
    const buffer = new ArrayBuffer(44 + dataBytes);
    const view = new DataView(buffer);
    const writeStr = (pos, str) => { for (let i = 0; i < str.length; i++) view.setUint8(pos + i, str.charCodeAt(i)); };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + dataBytes, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, targetRate, true);
    view.setUint32(28, targetRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, dataBytes, true);
    let p = 44;
    for (let i = 0; i < resampled.length; i++) {
      const s = Math.max(-1, Math.min(1, resampled[i]));
      view.setInt16(p, s < 0 ? s * 0x8000 : s * 0x7FFF, true); p += 2;
    }
    return new Blob([buffer], { type: 'audio/wav' });
  };

  // Pick the best MediaRecorder mimeType that both iOS and Android can play.
  // Priority: audio/mp4 (AAC) — works on iOS Safari natively + Chrome Android 108+
  //           → then webm/opus as fallback (Chrome only, won't play on iOS)
  //           → let browser pick otherwise
  const pickRecorderMime = () => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
    const candidates = [
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/aac',
      'audio/webm;codecs=opus',
      'audio/webm',
    ];
    for (const m of candidates) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* noop */ }
    }
    return '';
  };

  // Enqueue incoming walkie-talkie messages into the unified playback
  // queue and (best-effort) kick the single worker. Dedupes by `msg.id`
  // against both what's already playing/played and what's already in
  // the queue — so repeated polls never double up.
  const enqueueWalkieInbox = useCallback((inbox) => {
    if (!Array.isArray(inbox) || inbox.length === 0) return;
    const inQueue = new Set(walkieQueueRef.current.map((m) => m.id));
    // Only keep messages from the last 45 s — anything older would feel
    // eerie to replay on a "real-time" channel. Preserves server order.
    const cutoff = Date.now() - 45_000;
    for (const msg of inbox) {
      if (!msg || !msg.id) continue;
      if (walkiePlayedIdsRef.current.has(msg.id)) continue;
      if (inQueue.has(msg.id)) continue;
      if (msg.created_at) {
        const t = new Date(msg.created_at).getTime();
        if (Number.isFinite(t) && t < cutoff) continue;
      }
      walkieQueueRef.current.push(msg);
      inQueue.add(msg.id);
    }
    startWalkieWorker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single-threaded playback worker. Guarantees strict sequential
  // playback with a 1 s silence gap between messages — regardless of
  // whether they arrived in the same poll batch or over time. Exits
  // silently when the queue is empty or when the user is recording.
  const startWalkieWorker = useCallback(async () => {
    if (walkieWorkerBusyRef.current) return;
    walkieWorkerBusyRef.current = true;
    // Timing telemetry — used by the testing agent to validate the
    // 1 s gap contract. Safe to leave in production: it's a handful of
    // log lines per walkie session, negligible cost.
    // eslint-disable-next-line no-console
    console.log('[walkie-worker] start', { queue_size: walkieQueueRef.current.length, ts: Date.now() });
    try {
      while (walkieQueueRef.current.length > 0) {
        if (walkieRecordingRef.current) {
          // Don't play while the mic is live — the recording stop
          // handler will re-kick us after its 1 s grace pause.
          // eslint-disable-next-line no-console
          console.log('[walkie-worker] paused (recording)', { ts: Date.now() });
          return;
        }
        const msg = walkieQueueRef.current.shift();
        if (!msg || !msg.id || walkiePlayedIdsRef.current.has(msg.id)) continue;
        walkiePlayedIdsRef.current.add(msg.id);
        // eslint-disable-next-line no-console
        console.log('[walkie-worker] play-start', { msg_id: msg.id, ts: Date.now() });
        setWalkieIncoming({
          from_user_name: msg.from_user_name,
          from_user_picture: msg.from_user_picture,
        });
        try {
          await playBeep();
          await new Promise((r) => setTimeout(r, 120));
          const el = walkieAudioElRef.current;
          if (el) {
            el.src = `data:${msg.mime_type || 'audio/mp4'};base64,${msg.audio_base64}`;
            el.volume = 1.0;
            el.muted = false;
            await new Promise((resolve) => {
              const done = () => { el.onended = null; el.onerror = null; resolve(); };
              el.onended = done;
              el.onerror = done;
              el.play().catch(done);
            });
          } else {
            const audio = new Audio(`data:${msg.mime_type || 'audio/mp4'};base64,${msg.audio_base64}`);
            audio.volume = 1.0;
            await new Promise((resolve) => {
              audio.onended = resolve;
              audio.onerror = resolve;
              audio.play().catch(resolve);
            });
          }
        } catch { /* ignore playback errors */ }
        ackWalkieTalkie(msg.id).catch(() => {});
        // eslint-disable-next-line no-console
        console.log('[walkie-worker] play-end', { msg_id: msg.id, ts: Date.now() });
        // 1 s silence gap between messages — feels natural on a
        // walkie-talkie channel and gives the UI a beat to breathe.
        // Skip the gap only when the queue is empty (nothing next).
        if (walkieQueueRef.current.length > 0 && !walkieRecordingRef.current) {
          // eslint-disable-next-line no-console
          console.log('[walkie-worker] gap-start', { ts: Date.now() });
          await new Promise((r) => setTimeout(r, 1000));
          // eslint-disable-next-line no-console
          console.log('[walkie-worker] gap-end', { ts: Date.now() });
        }
      }
    } finally {
      walkieWorkerBusyRef.current = false;
      // eslint-disable-next-line no-console
      console.log('[walkie-worker] exit', { ts: Date.now() });
      setTimeout(() => {
        // Clear the "Sta parlando…" banner only if nothing else is
        // queued/playing by the time the fade-out fires.
        if (walkieQueueRef.current.length === 0 && !walkieWorkerBusyRef.current) {
          setWalkieIncoming(null);
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back-compat alias: the polling tick below still calls
  // `playWalkieInbox(inbox)`. Keep the same signature so we don't have
  // to touch the poll site — it now just enqueues into the worker.
  const playWalkieInbox = enqueueWalkieInbox;

  // True when the app runs inside the Capacitor Android WebView.
  // Android Chromium does NOT update `track.muted` in real-time after
  // `enabled = false`, so the "reuse the same stream" trick used on
  // iOS hands back a ghost-silent stream and the second/third walkie
  // tap captures only zeros. We branch on this flag: on Android we
  // ALWAYS request a fresh getUserMedia and hard-stop tracks on
  // release. On iOS we keep the original behaviour (cached stream +
  // soft release) which works fine there.
  const isAndroidNative = useMemo(() => {
    try {
      const plat = (typeof Capacitor !== 'undefined' && Capacitor?.getPlatform?.()) || '';
      return plat === 'android';
    } catch (_) { return false; }
  }, []);

  // Acquire (once) and reuse the persistent mic stream. When the stream
  // is still alive, just re-enable its tracks; otherwise request a fresh
  // one (which will prompt the user — only the first time per session).
  const acquireMicStream = useCallback(async () => {
    // Android: never reuse — always request a fresh stream. The permission
    // is cached at the OS level after the first grant, so this does NOT
    // re-prompt the user; it only sidesteps Chromium's stale-muted-track
    // bug that breaks the second walkie press.
    if (isAndroidNative) {
      try { persistentAudioStreamRef.current?.getAudioTracks?.().forEach((t) => t.stop()); } catch (_) {}
      persistentAudioStreamRef.current = null;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        persistentAudioStreamRef.current = stream;
        return stream;
      } catch (e) {
        const name = e?.name || 'UnknownError';
        const msg = e?.message || String(e);
        console.error('[Walkie/Android] getUserMedia failed:', name, msg);
        throw e;
      }
    }
    const existing = persistentAudioStreamRef.current;
    if (existing) {
      const tracks = existing.getAudioTracks();
      // A stream is reusable only if EVERY track is live AND not muted.
      // `track.muted = true` is how WebKit signals that the user revoked
      // permission via iOS Control Center / Settings while we still
      // had the stream cached. In that state the track keeps
      // `readyState === 'live'` (browsers don't end the track) so we
      // MUST also probe `muted` — otherwise we hand back a silent
      // stream and the recording captures only zeros.
      const stillUsable = tracks.length > 0 && tracks.every((t) =>
        t.readyState === 'live' && !t.muted
      );
      if (stillUsable) {
        tracks.forEach((t) => { t.enabled = true; });
        return existing;
      }
      // Stream is dead OR muted by the OS. Stop everything and request
      // a fresh getUserMedia — this re-triggers the iOS permission
      // prompt when permission was revoked, restoring the recording
      // flow without the user having to leave the app.
      try { tracks.forEach((t) => t.stop()); } catch (_) {}
      persistentAudioStreamRef.current = null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      persistentAudioStreamRef.current = stream;
      return stream;
    } catch (e) {
      const name = e?.name || 'UnknownError';
      const msg = e?.message || String(e);
      console.error('[Walkie] getUserMedia failed:', name, msg);
      throw e;
    }
  }, [isAndroidNative]);

  // Disable tracks (but do NOT stop them) so the mic light / iOS red bar
  // disappears between recordings while we keep the permission cached.
  // On Android we HARD STOP instead — the disable-only path leaves a
  // ghost-muted track that the next acquireMicStream would re-use, and
  // Chromium does not refresh `muted` so the recorder records silence.
  const releaseMicStreamSoft = useCallback(() => {
    const stream = persistentAudioStreamRef.current;
    if (!stream) return;
    if (isAndroidNative) {
      try { stream.getAudioTracks().forEach((t) => t.stop()); } catch (_) {}
      persistentAudioStreamRef.current = null;
      return;
    }
    try { stream.getAudioTracks().forEach((t) => { t.enabled = false; }); } catch (_) {}
  }, [isAndroidNative]);

  // Hard stop on unmount / page exit — frees the OS resource.
  useEffect(() => {
    return () => {
      const stream = persistentAudioStreamRef.current;
      if (stream) {
        try { stream.getAudioTracks().forEach((t) => t.stop()); } catch (_) {}
        persistentAudioStreamRef.current = null;
      }
    };
  }, []);

  const startWalkieRecording = async () => {
    setWalkieError('');
    setWalkieSending(false);
    setWalkieCountdown(5);
    // If a peer message is currently playing, stop it immediately so
    // the mic doesn't pick up our own audio output. It will NOT be
    // re-played because walkiePlayedIdsRef already marks it as seen —
    // this matches the "walkie-talkie" behaviour of real devices:
    // whoever started recording takes the channel.
    try {
      const el = walkieAudioElRef.current;
      if (el && !el.paused) {
        el.pause();
        el.currentTime = 0;
        el.src = '';
      }
    } catch (_) { /* ignore */ }
    let stream;
    try {
      stream = await acquireMicStream();
      // Defensive: even after a successful getUserMedia, iOS WebKit can
      // hand us a track that is immediately muted if the OS-level mic
      // is still locked. Treat that as a permission failure so the user
      // gets actionable feedback instead of a silent failed send.
      const audioTracks = stream?.getAudioTracks?.() || [];
      if (!audioTracks.length || audioTracks.some((t) => t.muted || t.readyState !== 'live')) {
        try { audioTracks.forEach((t) => t.stop()); } catch (_) {}
        persistentAudioStreamRef.current = null;
        throw new Error('mic_muted_by_os');
      }
    } catch (e) {
      // NotAllowedError → user denied the prompt. Other errors → mic
      // hardware/OS-level block. Either way, point them to Settings
      // since iOS will NOT re-prompt within the same session after a
      // hard deny.
      const name = e?.name || '';
      console.error('[Walkie] startWalkieRecording mic error:', name, e?.message || e);
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setWalkieError(t('walk.walkieMicDeniedSettings') || 'Accesso al microfono negato. Apri Impostazioni → Slimmy → Microfono per abilitarlo.');
      } else if (name === 'NotReadableError') {
        setWalkieError(t('walk.walkieMicBusy', 'Microfono in uso da un\'altra app. Chiudila e riprova.'));
      } else if (name === 'AbortError') {
        setWalkieError(t('walk.walkieMicAbort', 'Richiesta microfono annullata. Riprova.'));
      } else if (String(e?.message || '').includes('mic_muted_by_os')) {
        setWalkieError(t('walk.walkieMicMuted', 'Microfono silenziato dal sistema. Riprova.'));
      } else {
        setWalkieError(t('walk.walkieMicDenied') || 'Accesso al microfono negato');
      }
      return;
    }

    const mime = pickRecorderMime();
    // ── Primary path: MediaRecorder with the best cross-platform codec ──
    if (typeof MediaRecorder !== 'undefined') {
      let recorder;
      try {
        recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch {
        recorder = null;
      }
      if (recorder) {
        const blobChunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            blobChunks.push(e.data);
            walkieDebug(`mic:ondataavailable size=${e.data.size} chunks=${blobChunks.length}`);
          }
        };
        recorder.onstop = async () => {
          walkieDebug(`mic:onstop chunks=${blobChunks.length} abort=${walkieAbortPendingRef.current}`);
          // Hard-stop the mic tracks immediately — never reuse the stream.
          // This avoids the Android "ghost-muted track" bug for the next press.
          try { releaseMicStreamSoft(); } catch { /* noop */ }
          // Abort flag set when user did a quick-tap (<500ms) or the page
          // was hidden / lost focus — skip the upload entirely.
          if (walkieAbortPendingRef.current) {
            walkieAbortPendingRef.current = false;
            setWalkieRecording(false);
            setWalkieSending(false);
            walkieRecordingRef.current = false;
            walkieMediaRef.current = null;
            walkieDebug('mic:aborted (skip send)');
            return;
          }
          const effectiveMime = (blobChunks[0] && blobChunks[0].type) || mime || 'audio/mp4';
          const blob = new Blob(blobChunks, { type: effectiveMime });
          if (blob.size === 0) {
            walkieDebug('mic:error empty-blob');
            setWalkieError(t('walk.walkieEmpty') || 'Audio vuoto');
            setWalkieRecording(false);
            walkieRecordingRef.current = false;
            walkieMediaRef.current = null;
            return;
          }
          setWalkieSending(true);
          try {
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            const sendMime = (effectiveMime || 'audio/mp4').split(';')[0];
            await sendWalkieTalkie(base64, sendMime);
            walkieDebug(`mic:sent bytes=${blob.size} mime=${sendMime}`);
            setWalkieModalOpen(false);
          } catch (err) {
            walkieDebug(`mic:error send ${err?.message}`);
            setWalkieError(err?.response?.data?.detail || err?.message || 'Errore invio');
          } finally {
            setWalkieSending(false);
            setWalkieRecording(false);
            // Belt-and-braces: reset BOTH refs (state sync via
            // useEffect at line 154 has a tick lag; user's next tap
            // fires before that tick and hits stale truthy refs → the
            // "second tap does nothing" regression).
            walkieRecordingRef.current = false;
            walkieMediaRef.current = null;
          }
        };
        walkieMediaRef.current = {
          stopped: false,
          stop: () => {
            try { recorder.state === 'recording' && recorder.stop(); } catch { /* noop */ }
          },
          finalize: () => { /* onstop handles it */ },
        };
        try {
          recorder.start();
        } catch (e) {
          setWalkieError(t('walk.walkieNotSupported') || 'Registrazione non supportata su questo dispositivo');
          releaseMicStreamSoft();
          // Also reset the ref that we set on line ~1789 BEFORE start()
          // was attempted, otherwise the next press's guard
          // `walkieMediaRef.current` stays truthy and blocks forever.
          walkieMediaRef.current = null;
          walkieRecordingRef.current = false;
          setWalkieRecording(false);
          return;
        }
        setWalkieRecording(true);
        const startAt = Date.now();
        if (walkieCountdownIntRef.current) clearInterval(walkieCountdownIntRef.current);
        walkieCountdownIntRef.current = setInterval(() => {
          const left = Math.max(0, 10 - Math.floor((Date.now() - startAt) / 1000));
          setWalkieCountdown(left);
          if (left <= 0) {
            clearInterval(walkieCountdownIntRef.current);
            walkieCountdownIntRef.current = null;
            const r = walkieMediaRef.current;
            if (r && !r.stopped) { r.stopped = true; r.stop(); }
          }
        }, 250);
        return;
      }
    }

    // ── Fallback path: Web Audio + WAV (for ancient browsers w/o MediaRecorder) ──
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      setWalkieError(t('walk.walkieNotSupported') || 'Registrazione non supportata su questo dispositivo');
      releaseMicStreamSoft();
      return;
    }
    let audioCtx = walkieAudioCtxRef.current;
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new Ctx();
      walkieAudioCtxRef.current = audioCtx;
    }
    if (audioCtx.state === 'suspended') {
      try { await audioCtx.resume(); } catch { /* noop */ }
    }
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    const chunks = [];
    let sampleCount = 0;
    processor.onaudioprocess = (e) => {
      const d = e.inputBuffer.getChannelData(0);
      const copy = new Float32Array(d.length);
      copy.set(d);
      chunks.push(copy);
      sampleCount += d.length;
    };
    const silent = audioCtx.createGain();
    silent.gain.value = 0;
    source.connect(processor);
    processor.connect(silent);
    silent.connect(audioCtx.destination);

    walkieMediaRef.current = {
      stopped: false,
      stop: () => {
        try { processor.disconnect(); source.disconnect(); silent.disconnect(); } catch { /* noop */ }
        try { releaseMicStreamSoft(); } catch { /* noop */ }
      },
      finalize: async () => {
        if (sampleCount === 0) {
          setWalkieError(t('walk.walkieEmpty') || 'Audio vuoto');
          setWalkieRecording(false);
          walkieRecordingRef.current = false;
          walkieMediaRef.current = null;
          return;
        }
        const blob = encodeWav(chunks, audioCtx.sampleRate);
        setWalkieSending(true);
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          await sendWalkieTalkie(base64, 'audio/wav');
          setWalkieModalOpen(false);
        } catch (err) {
          setWalkieError(err?.response?.data?.detail || err?.message || 'Errore invio');
        } finally {
          setWalkieSending(false);
          setWalkieRecording(false);
          // Belt-and-braces: also imperatively reset the refs used by
          // startGesture's double-start guard (same rationale as the
          // MediaRecorder path above).
          walkieRecordingRef.current = false;
          walkieMediaRef.current = null;
        }
      },
    };
    setWalkieRecording(true);
    const startAt = Date.now();
    if (walkieCountdownIntRef.current) clearInterval(walkieCountdownIntRef.current);
    walkieCountdownIntRef.current = setInterval(() => {
      const left = Math.max(0, 10 - Math.floor((Date.now() - startAt) / 1000));
      setWalkieCountdown(left);
      if (left <= 0) {
        clearInterval(walkieCountdownIntRef.current);
        walkieCountdownIntRef.current = null;
        const r = walkieMediaRef.current;
        if (r && !r.stopped) { r.stopped = true; r.stop(); r.finalize(); }
      }
    }, 250);
  };

  const handleWalkieTalkieClick = () => {
    if (walkieModalOpen) return;
    const activeCount = activePeers.filter(p => p.is_active).length;
    if (activeCount === 0) return;
    setWalkieModalOpen(true);
    setWalkieError('');
    setTimeout(() => startWalkieRecording(), 150);
  };

  const handleWalkieCancel = () => {
    const r = walkieMediaRef.current;
    try {
      if (r && !r.stopped) {
        r.stopped = true;
        r.stop();
      }
    } catch { /* noop */ }
    if (walkieCountdownIntRef.current) {
      clearInterval(walkieCountdownIntRef.current);
      walkieCountdownIntRef.current = null;
    }
    walkieMediaRef.current = null;
    setWalkieRecording(false);
    setWalkieSending(false);
    setWalkieModalOpen(false);
    setWalkieCountdown(10);
  };

  // Send-now: user wants to dispatch the voice note before the 10s timer ends.
  // Works for both MediaRecorder (onstop handles the upload) and the
  // Web-Audio fallback (finalize triggers encoding + upload).
  const handleWalkieSendNow = () => {
    const r = walkieMediaRef.current;
    if (!r || r.stopped) return;
    if (walkieCountdownIntRef.current) {
      clearInterval(walkieCountdownIntRef.current);
      walkieCountdownIntRef.current = null;
    }
    r.stopped = true;
    try { r.stop(); } catch { /* noop */ }
    // MediaRecorder path: finalize is a no-op because `onstop` does the work.
    // Fallback path: finalize is the one that actually encodes and uploads.
    try { r.finalize && r.finalize(); } catch { /* noop */ }
  };

  // WhatsApp-style press-and-hold on the Rec button:
  //   press  → start recording + show 10s progress bar on the button
  //   release → stop + send (same path as the "Invia ora" modal button)
  //   10s elapsed without release → auto-stop + send (existing countdown)
  // When the user holds, we skip the modal to keep the UI clean — the
  // progress bar on the button itself is enough visual feedback.
  const [walkieHoldMode, setWalkieHoldMode] = useState(false);
  // Visual progress driven directly by an RAF loop that starts at the
  // instant of press-down. Decoupled from `walkieCountdown` (which lags
  // behind the mic-permission prompt) so the circle ALWAYS starts full
  // and drains smoothly without any "jump" artifacts.
  const [walkieHoldProgress, setWalkieHoldProgress] = useState(0); // 0..100
  const walkieHoldStartRef = useRef(0);
  const walkieHoldRafRef = useRef(null);
  // Android race fix: if the user releases the button BEFORE the recorder
  // has actually started (mic permission prompt + getUserMedia can take
  // several hundred ms on Chromium WebView), we mark a "pending release"
  // and auto-send the moment the recorder comes online. Without this,
  // the overlay used to flash open and close immediately because the
  // pointerup handler tore down walkieHoldMode while walkieRecording was
  // still false.
  const walkiePendingReleaseRef = useRef(false);
  const walkiePendingReleaseTimerRef = useRef(null);
  // Abort flag: set when user did a quick tap (<400ms). When the
  // recorder finally comes online, immediately stop it WITHOUT
  // sending, then close the overlay.
  const walkieAbortPendingRef = useRef(false);
  // Android: store document-level release listeners so we can clean them
  // up reliably. setPointerCapture on Chromium WebView is unreliable
  // (the capture is silently dropped if the WebView decides the gesture
  // could be a scroll), causing pointerup to never fire on the button.
  // We sidestep this by listening for pointerup/cancel/touchend on the
  // document while the hold is active.
  const walkieDocReleaseHandlerRef = useRef(null);
  // Dedupe flag: both React handlers and document listeners may invoke
  // handleWalkieHoldEnd for the same release; ensure side-effects run
  // exactly once.
  const walkieHoldEndedRef = useRef(false);
  // Mirror walkieHoldMode in a ref so handleWalkieHoldEnd (captured by
  // document listeners registered during handleWalkieHoldStart) always
  // sees the latest value — React state updates are async, so reading
  // walkieHoldMode directly from closure returns the STALE pre-press
  // value (false) and aborts the release handler instantly.
  const walkieHoldModeRef = useRef(false);

  const stopWalkieHoldRaf = () => {
    if (walkieHoldRafRef.current) {
      cancelAnimationFrame(walkieHoldRafRef.current);
      walkieHoldRafRef.current = null;
    }
  };

  // Android Chromium WebView fires BOTH the touch* event AND the
  // synthetic mouse* event for the same finger gesture (even when the
  // touch handler calls preventDefault — React adds touch listeners
  // as passive by default, so preventDefault is silently ignored).
  // This caused the press-and-hold mic button to open the recording
  // overlay and immediately close it: touchstart starts the recording,
  // then the synthetic mousedown re-invokes the same handler while the
  // touch is still down, racing with handleWalkieHoldEnd. We dedupe
  // by remembering when a touch event last fired and ignoring any
  // mouse event within a 600ms window after it (Chromium delivers the
  // synthetic mouse events typically within ~300ms of touchend).
  const lastTouchAtRef = useRef(0);
  const wrapTouchEvent = (handler) => (e) => {
    const type = e?.type || 'unknown';
    if (e?.type?.startsWith?.('touch')) {
      lastTouchAtRef.current = Date.now();
      walkieDebug(`evt:${type}`);
    } else if (e?.type?.startsWith?.('mouse')) {
      if (Date.now() - lastTouchAtRef.current < 600) {
        walkieDebug(`evt:${type} SUPPRESSED`);
        return; // suppress synthetic
      }
      walkieDebug(`evt:${type}`);
    }
    return handler(e);
  };

  const handleWalkieHoldStart = async (e) => {
    walkieDebug(`HoldStart:${e?.type||'?'}`);
    // Prevent iOS Safari from firing a synthetic click after a touchstart.
    if (e && e.preventDefault) e.preventDefault();
    // Reset dedupe flag for this new gesture.
    walkieHoldEndedRef.current = false;
    // Reset any leftover pending-release / abort state from a previous gesture.
    walkiePendingReleaseRef.current = false;
    walkieAbortPendingRef.current = false;
    if (walkiePendingReleaseTimerRef.current) {
      clearTimeout(walkiePendingReleaseTimerRef.current);
      walkiePendingReleaseTimerRef.current = null;
    }
    if (walkieRecording || walkieSending) return;
    if (!activePeers.some((p) => p.is_active)) return;

    // NOTE: removed the previous "double-tap for permission" fallback
    // (first tap = prompt, second tap = record). It was unreliable on
    // iOS WebKit: press-1 asked the OS prompt then set a ready-hint
    // and returned. Even holding the button through the prompt did
    // NOT start recording, and the second press often did nothing at
    // all because state left over from the aborted first press
    // (walkiePermissionGrantedRef true + null stream + short-circuit
    // guards elsewhere) blocked the retry. We now go straight into
    // recording; iOS caches the permission across app launches, so
    // this is a one-time prompt for the very first walk after install,
    // and every subsequent tap records immediately with no dance.

    setWalkieHoldMode(true);
    walkieHoldModeRef.current = true;
    setWalkieError('');
    setWalkieCountdown(10);
    // Reset visual progress to 0 (full circle) IMMEDIATELY so the first
    // paint of the overlay shows a complete ring — no lag behind the
    // getUserMedia prompt, no stale value from a previous session.
    setWalkieHoldProgress(0);
    walkieHoldStartRef.current = Date.now();
    stopWalkieHoldRaf();
    const tick = () => {
      const elapsed = Date.now() - walkieHoldStartRef.current;
      const pct = Math.max(0, Math.min(100, (elapsed / 10000) * 100));
      setWalkieHoldProgress(pct);
      if (pct < 100) {
        walkieHoldRafRef.current = requestAnimationFrame(tick);
      } else {
        walkieHoldRafRef.current = null;
      }
    };
    walkieHoldRafRef.current = requestAnimationFrame(tick);
    // Kick off the recorder directly — the existing startWalkieRecording
    // sets up the 10s countdown that will auto-send if the user never
    // releases (same contract as the modal-based flow).
    try {
      await startWalkieRecording();
      // If startWalkieRecording set an error (e.g. mic_muted_by_os) it
      // returned without starting the recorder. Reset the hold overlay
      // so the user can try again immediately instead of being stuck
      // with a frozen mic-icon that never records.
      if (!walkieRecordingRef.current) {
        setWalkieHoldMode(false);
        walkieHoldModeRef.current = false;
        stopWalkieHoldRaf();
        setWalkieHoldProgress(0);
      }
    } catch (err) {
      console.error('[Walkie] handleWalkieHoldStart startRecording threw:', err);
      setWalkieHoldMode(false);
      walkieHoldModeRef.current = false;
      stopWalkieHoldRaf();
      setWalkieHoldProgress(0);
    }
  };

  const handleWalkieHoldEnd = (e) => {
    walkieDebug(`HoldEnd:${e?.type||'?'} holdMode=${walkieHoldModeRef.current} rec=${walkieRecordingRef.current} ended=${walkieHoldEndedRef.current}`);
    if (!walkieHoldModeRef.current) return;
    // Dedupe: both the React onPointerUp/Cancel AND the document-level
    // listener may fire for the same release. We guard via a ref since
    // setWalkieHoldMode is async (state hasn't flushed yet).
    if (walkieHoldEndedRef.current) return;
    const heldMs = Date.now() - walkieHoldStartRef.current;
    // Android Chromium can fire a spurious `pointercancel` shortly after
    // `pointerdown` (scroll-gesture heuristics, scale animation, etc.).
    // Ignore very early cancels — wait for the real `pointerup`.
    if (e?.type === 'pointercancel' && heldMs < 700) {
      return;
    }
    walkieHoldEndedRef.current = true;
    // If the user released BEFORE the recorder has actually started
    // (mic prompt + getUserMedia latency on Android), DO NOT tear down
    // the overlay yet. Mark a pending release; the effect below will
    // auto-send the moment walkieRecording becomes true.
    // Read from the REF to always get the LATEST value.
    const recordingNow = walkieRecordingRef.current;
    if (!recordingNow) {
      if (heldMs < 400) {
        // Quick tap — abort: close overlay now, and if the recorder
        // comes online later, stop it without sending.
        walkieAbortPendingRef.current = true;
        setWalkieHoldMode(false);
        stopWalkieHoldRaf();
        return;
      }
      walkiePendingReleaseRef.current = true;
      if (walkiePendingReleaseTimerRef.current) {
        clearTimeout(walkiePendingReleaseTimerRef.current);
      }
      walkiePendingReleaseTimerRef.current = setTimeout(() => {
        walkiePendingReleaseRef.current = false;
        walkiePendingReleaseTimerRef.current = null;
        setWalkieHoldMode(false);
        stopWalkieHoldRaf();
      }, 4000);
      return;
    }
    setWalkieHoldMode(false);
    stopWalkieHoldRaf();
    handleWalkieSendNow();
  };

  // When the recorder finally comes online after a pending early
  // release OR a quick-tap abort, handle accordingly.
  useEffect(() => {
    if (!walkieRecording) return;
    if (walkieAbortPendingRef.current) {
      walkieAbortPendingRef.current = false;
      // Stop the recorder WITHOUT triggering send path.
      const r = walkieMediaRef.current;
      if (r && !r.stopped) {
        r.stopped = true;
        try { r.stop(); } catch (_) {}
      }
      if (walkieCountdownIntRef.current) {
        clearInterval(walkieCountdownIntRef.current);
        walkieCountdownIntRef.current = null;
      }
      // Manually mark recording as ended so the safety useEffect
      // can also close the overlay if it didn't already.
      setWalkieRecording(false);
      setWalkieHoldMode(false);
      stopWalkieHoldRaf();
      return;
    }
    if (walkiePendingReleaseRef.current) {
      walkiePendingReleaseRef.current = false;
      if (walkiePendingReleaseTimerRef.current) {
        clearTimeout(walkiePendingReleaseTimerRef.current);
        walkiePendingReleaseTimerRef.current = null;
      }
      setWalkieHoldMode(false);
      stopWalkieHoldRaf();
      handleWalkieSendNow();
    }
  }, [walkieRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep walkieHoldModeRef in sync with state for code paths other
  // than handleWalkieHoldEnd (safety timeout, pending-release effect,
  // recording aborted, etc.). The existing walkieRecordingRef
  // (declared near the top of this component) is already kept in
  // sync via its own useEffect.
  useEffect(() => {
    walkieHoldModeRef.current = walkieHoldMode;
  }, [walkieHoldMode]);

  // ============================================================
  // NATIVE ANDROID WALKIE-TALKIE PLUGIN INTEGRATION
  // ============================================================
  // Android Capacitor WebView cannot reliably handle press-and-hold
  // (cancels gesture after ~150ms regardless of any JS workaround).
  // We use a custom native plugin that draws a transparent View
  // ON TOP of the JS button and receives Android MotionEvents
  // directly + records audio via native MediaRecorder.
  //
  // iOS and PWA keep the existing WebView-based logic unchanged.
  useEffect(() => {
    if (!isWalkieRecorderAvailable()) return;
    if (status !== 'tracking' && status !== 'paused') return;

    const btn = status === 'paused' ? walkieBtnPausedRef.current : walkieBtnRef.current;
    if (!btn) return;

    let mounted = true;
    const subs = [];
    let lastRect = { x: -1, y: -1, w: -1, h: -1 };
    let debounceTimer = null;

    const doPosition = async () => {
      if (!mounted) return;
      const rect = btn.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const next = {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        w: Math.round(rect.width),
        h: Math.round(rect.height),
      };
      // Skip if coords haven't changed — avoids spamming the native
      // bridge when window.resize fires on every WebView layout shift.
      if (next.x === lastRect.x && next.y === lastRect.y
          && next.w === lastRect.w && next.h === lastRect.h) {
        return;
      }
      lastRect = next;
      try {
        await showWalkieButton({ x: next.x, y: next.y, width: next.w, height: next.h });
      } catch (e) {
        walkieDebug(`native:showButton-error ${e?.message || e}`);
      }
    };

    // Debounced wrapper: collapses bursts of resize/orientation events
    // into a single call after a 200ms quiet window.
    const positionOverlay = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(doPosition, 200);
    };

    // Initial position (after one frame so DOM has correct layout);
    // reposition only on resize/orientation (debounced + dedup).
    requestAnimationFrame(() => doPosition());
    window.addEventListener('resize', positionOverlay);
    window.addEventListener('orientationchange', positionOverlay);

    // NB: NON richiediamo il permesso microfono qui all'apertura.
    // Il plugin nativo lo richiede ON-DEMAND alla prima ACTION_DOWN
    // del bottone walkie. Comportamento UX equivalente a WhatsApp.

    // Listeners for native plugin events
    onNativeWalkieStarted(() => {
      walkieDebug('native:recordingStarted');
      setWalkieHoldMode(true);
      walkieHoldModeRef.current = true;
      setWalkieError('');
      // Animate the red progress ring (10s window). We mirror the same
      // logic used in startWalkieRecording for iOS/PWA: progress goes
      // 0 → 100 over 10 seconds via requestAnimationFrame.
      walkieHoldStartRef.current = Date.now();
      stopWalkieHoldRaf();
      const tick = () => {
        const elapsed = Date.now() - walkieHoldStartRef.current;
        const pct = Math.min(100, (elapsed / 10000) * 100);
        setWalkieHoldProgress(pct);
        setWalkieCountdown(Math.max(0, 10 - Math.floor(elapsed / 1000)));
        if (elapsed < 10000 && walkieHoldModeRef.current) {
          walkieHoldRafRef.current = requestAnimationFrame(tick);
        }
      };
      walkieHoldRafRef.current = requestAnimationFrame(tick);
    }).then((s) => subs.push(s));

    onNativeWalkieStopped(async (data) => {
      const durationMs = data?.durationMs || 0;
      const base64 = data?.base64;
      const mimeType = data?.mimeType || 'audio/mp4';
      const bytes = data?.bytes || 0;
      walkieDebug(`native:recordingStopped duration=${durationMs} bytes=${bytes}`);
      setWalkieHoldMode(false);
      walkieHoldModeRef.current = false;
      stopWalkieHoldRaf();
      setWalkieHoldProgress(0);
      if (!base64) return;
      setWalkieSending(true);
      try {
        await sendWalkieTalkie(base64, mimeType);
        walkieDebug('native:sent');
      } catch (err) {
        walkieDebug(`native:send-error ${err?.message || err}`);
        setWalkieError(err?.response?.data?.detail || err?.message || 'Errore invio');
      } finally {
        setWalkieSending(false);
      }
    }).then((s) => subs.push(s));

    onNativeWalkieCancelled((data) => {
      walkieDebug(`native:recordingCancelled duration=${data?.durationMs}`);
      setWalkieHoldMode(false);
      walkieHoldModeRef.current = false;
      stopWalkieHoldRaf();
      setWalkieHoldProgress(0);
      if ((data?.durationMs || 0) < 500) {
        setWalkieError(t('walk.walkieTalkieHoldLonger', 'Tieni premuto per registrare') + '');
        setTimeout(() => setWalkieError(''), 2000);
      }
    }).then((s) => subs.push(s));

    onNativeWalkieError((data) => {
      walkieDebug(`native:error ${data?.message || 'unknown'}`);
      setWalkieHoldMode(false);
      walkieHoldModeRef.current = false;
      setWalkieError(data?.message || 'Errore recorder');
    }).then((s) => subs.push(s));

    return () => {
      mounted = false;
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener('resize', positionOverlay);
      window.removeEventListener('orientationchange', positionOverlay);
      hideWalkieButton().catch(() => {});
      subs.forEach((s) => { try { s.remove(); } catch (_) {} });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Nascondi l'overlay nativo del WalkieRecorder mentre è aperto il
  // viewer fullscreen di una foto/video → altrimenti la View Android
  // (TYPE_APPLICATION_PANEL via WindowManager) sta SOPRA il fullscreen
  // viewer in z-order del compositor e intercetta i touch nella zona
  // del bottone mic (incluso il pulsante X di chiusura se si sovrappone).
  useEffect(() => {
    if (!isWalkieRecorderAvailable()) return;
    if (status !== 'tracking' && status !== 'paused') return;
    if (liveMediaViewer) {
      hideWalkieButton().catch(() => {});
      return () => {
        // Ripristina overlay quando il viewer si chiude.
        const btn = status === 'paused' ? walkieBtnPausedRef.current : walkieBtnRef.current;
        if (!btn) return;
        requestAnimationFrame(() => {
          const rect = btn.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return;
          showWalkieButton({
            x: rect.left, y: rect.top,
            width: rect.width, height: rect.height,
          }).catch(() => {});
        });
      };
    }
  }, [liveMediaViewer, status]);

  // ============================================================
  // PRODUCTION WALKIE-TALKIE GESTURE HANDLER (iOS + PWA only)
  // ============================================================
  // On Android Capacitor we use the native plugin above instead —
  // the WebView there cancels long-press gestures unreliably. iOS
  // and PWA browsers handle Pointer Events correctly so we keep
  // the JS-based logic for them.
  useEffect(() => {
    // Skip WebView gesture handling on Android Capacitor — the native
    // plugin handles all touch + recording natively.
    if (isWalkieRecorderAvailable()) {
      walkieDebug('skip JS gesture — native plugin active');
      return;
    }
    const buttons = [walkieBtnRef.current, walkieBtnPausedRef.current].filter(Boolean);
    walkieDebug(`attach pointer-handlers n=${buttons.length} status=${status}`);
    if (buttons.length === 0) return;

    // Per-gesture state (closed over by the handlers below)
    const gesture = {
      pointerId: null,
      touchId: null, // TouchEvent identifier — survives pointercancel
      startTime: 0,
      // When pointercancel fires while finger is still down, we fall
      // back to monitoring document-level touchend for the original
      // Touch.identifier. This sidesteps Chromium WebView's bug where
      // it cancels pointer events when the finger moves even slightly.
      pointerCancelled: false,
    };

    const isPeerActive = () => activePeers.some((p) => p.is_active);

    const startGesture = (e) => {
      // Block double-start: already recording OR a recorder is queued.
      if (walkieRecordingRef.current || walkieMediaRef.current) {
        walkieDebug('mic:double-start-blocked');
        return;
      }
      if (!isPeerActive()) {
        walkieDebug('mic:no-active-peers');
        return;
      }
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
      // Capture the pointer so subsequent events route to THIS button
      // even if the finger slides outside its bounding box.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}

      gesture.pointerId = e.pointerId;
      gesture.touchId = null; // will be set by onTouchStart listener below
      gesture.startTime = Date.now();
      gesture.pointerCancelled = false;

      // NUCLEAR OPTION: install a document-level CAPTURE-phase listener
      // for touchmove with preventDefault. This is the only way to
      // override Chromium WebView's compositor-level scroll/tap
      // detection — even if our button-level listeners don't fire in
      // time, the document capture listener runs FIRST in the event
      // path and can suppress the gesture interpretation entirely.
      const docTouchSuppress = (ev) => {
        try { ev.preventDefault(); } catch (_) {}
      };
      document.addEventListener('touchmove', docTouchSuppress, { passive: false, capture: true });
      gesture.docTouchSuppress = docTouchSuppress;
      walkieAbortPendingRef.current = false;
      walkiePendingReleaseRef.current = false;
      walkieHoldEndedRef.current = false;
      walkieHoldStartRef.current = gesture.startTime;

      walkieDebug(`mic:pointerdown id=${e.pointerId} type=${e.pointerType}`);
      setWalkieHoldMode(true);
      walkieHoldModeRef.current = true;

      // Countdown ring animation — identical to the RAF loop used in
      // handleWalkieHoldStart (lines ~2062-2073). Without this, the
      // outer circular progress indicator stays frozen while the
      // recorder is running.
      setWalkieHoldProgress(0);
      stopWalkieHoldRaf();
      const tick = () => {
        const elapsed = Date.now() - walkieHoldStartRef.current;
        const pct = Math.max(0, Math.min(100, (elapsed / 10000) * 100));
        setWalkieHoldProgress(pct);
        if (pct < 100) {
          walkieHoldRafRef.current = requestAnimationFrame(tick);
        } else {
          walkieHoldRafRef.current = null;
        }
      };
      walkieHoldRafRef.current = requestAnimationFrame(tick);

      // Kick off the async recorder pipeline.
      startWalkieRecording()
        .then(() => {
          walkieDebug(`mic:start-recording done rec=${walkieRecordingRef.current}`);
          // If user released while we were starting, resolve the pending action.
          if (walkieAbortPendingRef.current && walkieMediaRef.current) {
            walkieDebug('mic:applying pending-abort');
            try { walkieMediaRef.current.stop(); } catch (_) {}
          } else if (walkiePendingReleaseRef.current && walkieMediaRef.current) {
            walkieDebug('mic:applying pending-release');
            walkiePendingReleaseRef.current = false;
            try { walkieMediaRef.current.stop(); } catch (_) {}
          }
        })
        .catch((err) => {
          walkieDebug(`mic:error startRecording ${err?.message || err}`);
          setWalkieHoldMode(false);
          walkieHoldModeRef.current = false;
          // CRITICAL: on any startWalkieRecording failure (including
          // iOS OS-prompt cancel/timeout), reset ALL guards so the
          // next press isn't blocked. Also stop the countdown RAF.
          stopWalkieHoldRaf();
          setWalkieHoldProgress(0);
          walkieRecordingRef.current = false;
          walkieMediaRef.current = null;
          setWalkieRecording(false);
          setWalkieSending(false);
          walkieAbortPendingRef.current = false;
          walkiePendingReleaseRef.current = false;
        });
    };

    const endGesture = (reason, e) => {
      // Filter to our pointer (when applicable)
      if (e && gesture.pointerId !== null && e.pointerId !== undefined && e.pointerId !== gesture.pointerId) {
        return;
      }
      if (gesture.pointerId === null && !walkieHoldModeRef.current) {
        // No active gesture — ignore.
        return;
      }
      // Dedupe across button+window listeners
      if (walkieHoldEndedRef.current) return;
      walkieHoldEndedRef.current = true;

      // Remove document-level touchmove suppressor.
      if (gesture.docTouchSuppress) {
        document.removeEventListener('touchmove', gesture.docTouchSuppress, { capture: true });
        gesture.docTouchSuppress = null;
      }

      const heldMs = Date.now() - (gesture.startTime || Date.now());
      walkieDebug(`mic:end reason=${reason} heldMs=${heldMs} rec=${walkieRecordingRef.current}`);
      gesture.pointerId = null;

      // Recorder online?
      if (walkieMediaRef.current && walkieRecordingRef.current) {
        if (heldMs < 500) {
          walkieDebug('mic:cancelled (<500ms)');
          walkieAbortPendingRef.current = true;
          try { walkieMediaRef.current.stop(); } catch (_) {}
          setWalkieError(t('walk.walkieTalkieHoldLonger', 'Tieni premuto per registrare') + '');
          setTimeout(() => setWalkieError(''), 2000);
        } else {
          try { walkieMediaRef.current.stop(); } catch (_) {}
        }
        setWalkieHoldMode(false);
        walkieHoldModeRef.current = false;
        return;
      }
      // Recorder NOT online yet (still in getUserMedia).
      if (heldMs < 500) {
        // Mark abort — when recorder comes online it'll be stopped without send.
        walkieAbortPendingRef.current = true;
        // Close overlay immediately for visual feedback.
        setWalkieHoldMode(false);
        walkieHoldModeRef.current = false;
        walkieDebug('mic:pending-abort (<500ms)');
        return;
      }
      walkiePendingReleaseRef.current = true;
      // Safety: close overlay if recorder never starts within 4s.
      if (walkiePendingReleaseTimerRef.current) clearTimeout(walkiePendingReleaseTimerRef.current);
      walkiePendingReleaseTimerRef.current = setTimeout(() => {
        walkiePendingReleaseRef.current = false;
        walkiePendingReleaseTimerRef.current = null;
        if (!walkieRecordingRef.current) {
          walkieDebug('mic:pending-release timeout — closing overlay');
          setWalkieHoldMode(false);
          walkieHoldModeRef.current = false;
        }
      }, 4000);
    };

    // Per-button native listeners ─────────────────────────────────────
    const cleanups = [];
    let lastMoveAt = 0;
    let moveCount = 0;
    buttons.forEach((btn) => {
      const onDown = (ev) => { moveCount = 0; startGesture(ev); };
      const onMove = (ev) => {
        // Only count moves while gesture is active.
        if (gesture.pointerId === ev.pointerId) {
          moveCount++;
          if (moveCount % 5 === 1) {
            walkieDebug(`mic:pointermove n=${moveCount} dt=${Date.now() - gesture.startTime}`);
          }
          lastMoveAt = Date.now();
        }
      };
      const onUp = (ev) => {
        walkieDebug(`mic:pointerup-btn id=${ev.pointerId} moves=${moveCount}`);
        try { btn.releasePointerCapture(ev.pointerId); } catch (_) {}
        endGesture('pointerup', ev);
      };
      const onCancel = (ev) => {
        walkieDebug(`mic:pointercancel-btn id=${ev.pointerId} moves=${moveCount}`);
        // KEY FIX: if we got pointermove events, the finger is STILL on
        // the screen. Chromium WebView cancelled pointer tracking due to
        // its scroll-detection heuristic — but the touch is still active.
        // DO NOT end the gesture; instead mark pointer as cancelled and
        // rely on the document-level touchend listener below to detect
        // the actual finger lift via Touch.identifier.
        if (moveCount > 0) {
          gesture.pointerCancelled = true;
          walkieDebug('mic:pointercancel IGNORED — touch still active, awaiting touchend');
          return;
        }
        endGesture('pointercancel', ev);
      };
      const onLost = (ev) => {
        walkieDebug(`mic:lostpointercapture id=${ev.pointerId}`);
      };
      // TouchEvent fallback — survives pointercancel because the touch
      // sequence is tracked by Touch.identifier, not pointerId.
      // CRITICAL: touchstart AND touchmove must be non-passive with
      // preventDefault, otherwise Chromium WebView's scroll-detection
      // heuristic fires touchcancel on the document within ~200ms.
      const onTouchStart = (ev) => {
        // Capture the touch identifier for the current gesture.
        if (gesture.touchId === null && ev.changedTouches.length > 0) {
          gesture.touchId = ev.changedTouches[0].identifier;
          walkieDebug(`mic:touchstart-btn tid=${gesture.touchId}`);
        }
        try { ev.preventDefault(); } catch (_) {}
      };
      const onTouchMove = (ev) => {
        // Prevent the WebView from interpreting micro-movements as scroll
        // (which causes the entire pointer/touch sequence to be cancelled).
        try { ev.preventDefault(); } catch (_) {}
      };
      btn.addEventListener('pointerdown', onDown, { passive: false });
      btn.addEventListener('pointermove', onMove);
      btn.addEventListener('pointerup', onUp);
      btn.addEventListener('pointercancel', onCancel);
      btn.addEventListener('lostpointercapture', onLost);
      btn.addEventListener('touchstart', onTouchStart, { passive: false });
      btn.addEventListener('touchmove', onTouchMove, { passive: false });
      cleanups.push(() => {
        btn.removeEventListener('pointerdown', onDown);
        btn.removeEventListener('pointermove', onMove);
        btn.removeEventListener('pointerup', onUp);
        btn.removeEventListener('pointercancel', onCancel);
        btn.removeEventListener('lostpointercapture', onLost);
        btn.removeEventListener('touchstart', onTouchStart);
        btn.removeEventListener('touchmove', onTouchMove);
      });
    });

    // Document-level touchend / touchcancel fallback ─────────────────
    // Catches the finger-up when pointer events get cancelled by the
    // WebView scroll-detection. We match against the Touch.identifier
    // recorded during touchstart.
    const onDocTouchEnd = (ev) => {
      if (gesture.touchId === null) return;
      for (let i = 0; i < ev.changedTouches.length; i++) {
        if (ev.changedTouches[i].identifier === gesture.touchId) {
          walkieDebug(`mic:touchend-doc tid=${gesture.touchId} type=${ev.type}`);
          gesture.touchId = null;
          endGesture(`touch-${ev.type}`, null);
          return;
        }
      }
    };
    document.addEventListener('touchend', onDocTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onDocTouchEnd, { passive: true });
    cleanups.push(() => {
      document.removeEventListener('touchend', onDocTouchEnd);
      document.removeEventListener('touchcancel', onDocTouchEnd);
    });

    // Window-level fallback ───────────────────────────────────────────
    // Chromium WebView occasionally fires pointerup on the window
    // (or document) instead of on the captured element. We catch it
    // there as well — `endGesture` dedupes via walkieHoldEndedRef.
    // IMPORTANT: window-level `pointercancel` is treated like the
    // button-level one — if the touch is still active (moves>0 or
    // touchId still tracked), we IGNORE it and wait for the real
    // touchend on document.
    const winUp = (ev) => {
      if (gesture.pointerId === null) return;
      if (ev.pointerId !== gesture.pointerId) return;
      walkieDebug(`mic:pointerup-win id=${ev.pointerId}`);
      endGesture('window-pointerup', ev);
    };
    const winCancel = (ev) => {
      if (gesture.pointerId === null) return;
      if (ev.pointerId !== gesture.pointerId) return;
      walkieDebug(`mic:pointercancel-win id=${ev.pointerId} moves=${moveCount} touchId=${gesture.touchId}`);
      // Same logic as button-level: if the touch is still on screen
      // (we have a tracked touchId or saw any pointermove), this
      // pointercancel is just Chromium's scroll-heuristic — IGNORE.
      // The real release will come via document touchend below.
      if (gesture.touchId !== null || moveCount > 0) {
        gesture.pointerCancelled = true;
        walkieDebug('mic:pointercancel-win IGNORED — touch still active');
        return;
      }
      endGesture('window-pointercancel', ev);
    };
    window.addEventListener('pointerup', winUp);
    window.addEventListener('pointercancel', winCancel);
    cleanups.push(() => {
      window.removeEventListener('pointerup', winUp);
      window.removeEventListener('pointercancel', winCancel);
    });

    // Page-level safety: visibility / blur / pagehide ─────────────────
    const onVisChange = () => {
      if (document.hidden && (walkieRecordingRef.current || walkieHoldModeRef.current)) {
        walkieDebug('mic:visibility-hidden — abort');
        walkieAbortPendingRef.current = true;
        endGesture('visibility-hidden', null);
        if (walkieMediaRef.current) {
          try { walkieMediaRef.current.stop(); } catch (_) {}
        }
      }
    };
    const onPageHide = () => {
      if (walkieRecordingRef.current || walkieHoldModeRef.current) {
        walkieDebug('mic:pagehide — abort');
        walkieAbortPendingRef.current = true;
        endGesture('pagehide', null);
        if (walkieMediaRef.current) {
          try { walkieMediaRef.current.stop(); } catch (_) {}
        }
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('pagehide', onPageHide);
    cleanups.push(() => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('pagehide', onPageHide);
    });

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Safety reset: if the recording aborts for any reason (mic permission
  // denied, sendWalkieTalkie fails, etc.) clear hold-mode so the button
  // progress bar doesn't stay stuck.
  useEffect(() => {
    if (!walkieRecording && !walkieSending) {
      setWalkieHoldMode(false);
      stopWalkieHoldRaf();
    }
  }, [walkieRecording, walkieSending]);

  // ── Live Media (photo/video sharing during workout) ──

  // Decode a File into a base64 string (no data URL prefix).
  const fileToBase64 = useCallback((file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result || '';
      const i = String(s).indexOf(',');
      resolve(i >= 0 ? String(s).slice(i + 1) : String(s));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  }), []);

  // Compress a photo on the client before upload. iPhone full-resolution
  // shots come out 2-5 MB (sometimes 10+) which would exceed our backend
  // cap. We downscale to max 1600px on the long side and re-encode as
  // JPEG at 78% quality — plenty for the feed viewer, always well under
  // the 1.7 MB raw cap. Falls back to the original file on any error so
  // we don't block a user with a weird image.
  const compressPhoto = useCallback((file) => new Promise((resolve) => {
    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        try {
          // Target well below the 1.7 MB backend cap with a progressive
          // quality ladder. iPhone ProRAW/HEIC shots can be 5-10 MB so
          // we retry with smaller dimensions + lower quality until we
          // fit, rather than failing late after base64 inflation.
          const TARGET_BYTES = 1_400_000; // headroom vs 1.7 MB cap
          const ladder = [
            { maxSide: 1600, quality: 0.78 },
            { maxSide: 1280, quality: 0.70 },
            { maxSide: 1024, quality: 0.62 },
            { maxSide: 900,  quality: 0.55 },
            { maxSide: 800,  quality: 0.45 },
          ];
          const tryStep = (idx) => {
            if (idx >= ladder.length) {
              URL.revokeObjectURL(url);
              // Return the last attempt even if slightly over — the
              // caller will still check the final cap and reject.
              resolve(file);
              return;
            }
            const { maxSide, quality } = ladder[idx];
            const { width: W, height: H } = img;
            const scale = Math.min(1, maxSide / Math.max(W, H));
            const w = Math.round(W * scale);
            const h = Math.round(H * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob((blob) => {
              if (!blob) {
                tryStep(idx + 1);
                return;
              }
              if (blob.size <= TARGET_BYTES) {
                URL.revokeObjectURL(url);
                resolve(blob);
              } else {
                tryStep(idx + 1);
              }
            }, 'image/jpeg', quality);
          };
          tryStep(0);
        } catch (_) {
          URL.revokeObjectURL(url);
          resolve(file);
        }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch (_) {
      resolve(file);
    }
  }), []);

  const handleLiveMediaFile = useCallback(async (file, mediaType) => {
    if (!file) return;
    setLiveMediaError('');

    // For photos, auto-compress on the client before sizing checks.
    // iPhone shots are routinely > 2 MB which would otherwise be
    // rejected — users expect "take photo → share" to just work.
    let toUpload = file;
    let finalMime = file.type || 'image/jpeg';
    if (mediaType === 'photo') {
      const compressed = await compressPhoto(file);
      if (compressed && compressed.size) {
        toUpload = compressed;
        finalMime = compressed.type || 'image/jpeg';
      }
    }

    // Hard size guards mirroring backend caps (after compression)
    const maxBytes = mediaType === 'photo' ? 1_700_000 : 4_200_000;
    if (toUpload.size > maxBytes) {
      setLiveMediaError(t('walk.liveMediaTooBig', 'File troppo grande'));
      setLiveMediaOptionsOpen(false);
      return;
    }
    let durationSec = null;
    if (mediaType === 'video') {
      try {
        durationSec = await new Promise((resolve) => {
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.src = URL.createObjectURL(file);
          v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
          v.onerror = () => resolve(null);
        });
        if (durationSec && durationSec > 11) {
          setLiveMediaError(t('walk.liveMediaVideoTooLong', 'Video troppo lungo (max 10s)'));
          setLiveMediaOptionsOpen(false);
          return;
        }
      } catch (_) {}
    }

    setLiveMediaOptionsOpen(false);
    setLiveMediaUploading(true);
    try {
      const base64 = await fileToBase64(toUpload);
      const res = await sendLiveMedia(
        base64,
        mediaType,
        finalMime || (mediaType === 'photo' ? 'image/jpeg' : 'video/mp4'),
        durationSec,
      );
      vibrateMedium();
      const activeNow = res?.data?.peers_active_at_upload ?? 0;
      if (activeNow > 0) {
        toast.success(
          t('walk.liveMediaSentActive', { count: activeNow, defaultValue: `Condiviso con ${activeNow} amico/i attivi ora` })
        );
      } else {
        toast.success(
          t('walk.liveMediaSentDeferred', 'Nessun amico di sfida è attivo ora — il contenuto sarà visibile per 30 minuti')
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLiveMediaError(typeof detail === 'string' ? detail : (t('walk.liveMediaError', 'Errore invio')));
      toast.error(typeof detail === 'string' ? detail : (t('walk.liveMediaError', 'Errore invio')));
    } finally {
      setLiveMediaUploading(false);
    }
  }, [fileToBase64, compressPhoto, t]);

  // Open the fullscreen viewer for a peer who has unseen live media.
  const openPeerLiveMedia = useCallback(async (peer) => {
    try {
      const res = await getLiveMediaByOwner(peer.user_id);
      const items = res.data?.items || [];
      if (!items.length) {
        // Stale flag — refresh peers and bail
        setActivePeers(prev => prev.map(p => p.user_id === peer.user_id ? { ...p, has_live_media: false } : p));
        return;
      }
      setLiveMediaViewer({
        items,
        index: 0,
        ownerName: peer.user_name || '',
        ownerPicture: peer.user_picture || null,
        ownerUserId: peer.user_id,
      });
      vibrateMedium();
    } catch (err) {
      // 403 if no shared challenge (shouldn't happen here)
    }
  }, []);

  // Mark current viewer item as seen + advance / close
  const handleViewerNext = useCallback(async () => {
    if (!liveMediaViewer) return;
    const cur = liveMediaViewer.items[liveMediaViewer.index];
    if (cur?.media_id) {
      ackLiveMedia(cur.media_id).catch(() => {});
    }
    if (liveMediaViewer.index + 1 < liveMediaViewer.items.length) {
      setLiveMediaViewer({ ...liveMediaViewer, index: liveMediaViewer.index + 1 });
    } else {
      // All seen → clear flag locally for instant UX
      setActivePeers(prev => prev.map(p =>
        p.user_id === liveMediaViewer.ownerUserId ? { ...p, has_live_media: false, live_media_count: 0 } : p
      ));
      setLiveMediaViewer(null);
    }
  }, [liveMediaViewer]);

  const handleViewerClose = useCallback(() => {
    if (liveMediaViewer) {
      const cur = liveMediaViewer.items[liveMediaViewer.index];
      if (cur?.media_id) {
        ackLiveMedia(cur.media_id).catch(() => {});
      }
    }
    setLiveMediaViewer(null);
  }, [liveMediaViewer]);

  const handlePublish = async (text, challengeIds = []) => {
    setPublishing(true);
    try {
      // First save the workout
      const sessionToken = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/workouts`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'walk',
          distance_km: parseFloat(distance.toFixed(2)),
          duration_seconds: elapsedTime,
          calories: calories,
          positions: positions.slice(0, 500) // Save more positions for detail view
        })
      });
      
      // Then publish to feed, optionally scoped to one or more challenges
      const activityType = inferActivityType(distance, elapsedTime); // 'walk' | 'run'
      const isRun = activityType === 'run';
      const defaultText = isRun
        ? t('walk.shareTextRun', 'Ho completato una corsa di {distance} {unit} in {duration}! 🏃‍♂️')
            .replace('{distance}', convertDistance(distance).toFixed(2))
            .replace('{unit}', distanceUnit)
            .replace('{duration}', formatTime(elapsedTime))
        : t('walk.shareTextWalk', 'Ho completato una camminata di {distance} {unit} in {duration}! 🚶‍♂️')
            .replace('{distance}', convertDistance(distance).toFixed(2))
            .replace('{unit}', distanceUnit)
            .replace('{duration}', formatTime(elapsedTime));
      const postPayload = {
        post_type: 'camminata',
        text: text || defaultText,
        data: {
          distance: parseFloat(distance.toFixed(2)),
          duration_seconds: elapsedTime,
          calories: calories,
          positions: positions.slice(0, 100),
          activity_type: activityType,
        }
      };
      if (Array.isArray(challengeIds) && challengeIds.length > 0) {
        postPayload.challenge_ids = challengeIds;
      }
      await createPost(postPayload);
      navigate('/app/feed');
    } catch (err) {
      setError(t('walk.errors.publish'));
    } finally {
      setPublishing(false);
    }
  };

  // Save workout without publishing
  const handleSaveOnly = async () => {
    setPublishing(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/workouts`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'walk',
          distance_km: parseFloat(distance.toFixed(2)),
          duration_seconds: elapsedTime,
          calories: calories,
          positions: positions.slice(0, 500)
        })
      });
      navigate('/app/miei-allenamenti');
    } catch (err) {
      setError(t('walk.errors.save'));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className={`page-enter fixed inset-0 flex flex-col ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="camminata-page" style={{ zIndex: status === 'tracking' || status === 'paused' ? 200 : 'auto', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Fixed Header */}
      <div className={`flex-shrink-0 px-4 py-4 flex items-center gap-4 ${isDark ? 'bg-[#080D1A]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-sm`}>
        <div className="max-w-md mx-auto w-full flex items-center gap-4">
          {/* Back button - hidden when tracking/paused */}
          {status === 'idle' || status === 'finished' ? (
            <button 
              onClick={() => navigate(-1)}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDark ? 'bg-white/5' : 'bg-gray-100'
              }`}
            >
              <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
          ) : (
            <div className="w-10 h-10" /> 
          )}
          <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('walk.title')}
          </h1>
          
          {/* GPS Satellite Badge */}
          <div className="ml-auto flex items-center gap-1.5">
            {/* Live Walkers Badge */}
            {liveWalkers.enabled && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500/20 text-blue-600'
              }`}>
                <Users className="w-3.5 h-3.5" />
                <span>{liveWalkers.count}</span>
              </div>
            )}
            
            {/* Satellite Count Badge */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              satelliteCount >= 5 
                ? 'bg-green-500/20 text-green-500' 
                : satelliteCount >= 3 
                  ? 'bg-yellow-500/20 text-yellow-500'
                  : 'bg-red-500/20 text-red-500'
            }`}>
              <Satellite className="w-3.5 h-3.5" />
              <span>{status === 'idle' ? '-' : satelliteCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed content - no scroll */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Map — grows to fill the remaining vertical space so the page
            adapts to any device height (iPhone SE, iPhone Pro Max, iPad).
            A min-height ensures it stays usable on very short screens. */}
        <div className="px-4 pt-3 pb-2 flex-1 min-h-0 flex flex-col" style={{ zIndex: 1 }}>
        <div className="relative flex-1 min-h-0">
          <div 
            ref={mapRef}
            className={`rounded-2xl overflow-hidden w-full h-full ${
              isDark ? 'border border-white/10' : 'border border-gray-200'
            }`}
            style={{ minHeight: '180px', background: isDark ? '#1a1a2e' : '#f0f0f0' }}
          >
            {!mapReady && (
              <div className="w-full h-full flex items-center justify-center">
                <div className="spinner" />
              </div>
            )}
          </div>
          {/* Manual map refresh button — positioned at bottom-right
              (replaces the previous Slimmy logo watermark). Acts as
              a fallback if the auto reload-on-foreground doesn't kick
              in on the device. */}
          <div className="absolute bottom-2 right-2" style={{ zIndex: 1000 }}>
            <button
              onClick={manualReloadMap}
              aria-label="Ricarica mappa"
              title="Ricarica mappa"
              data-testid="map-manual-refresh-btn"
              className="w-9 h-9 rounded-2xl bg-black/55 backdrop-blur-sm text-white shadow-sm flex items-center justify-center active:scale-95 transition-transform"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Pace indicator — visible during an active session after the first 30s
          (gives GPS time to stabilize the speed estimate). 0–10 km/h scale
          with the green sweet-spot centered on 5 km/h (brisk walking).
          Uses the INSTANT GPS speed, not a running average, so a
          background gap (incoming call etc.) doesn't poison the reading. */}
      {(status === 'tracking' || status === 'paused') && elapsedTime >= 30 && (
        <div className="px-4 pb-2 flex-shrink-0">
          <PaceIndicator
            speedKmh={currentSpeedKmh}
            isDark={isDark}
            t={t}
          />
        </div>
      )}

      {/* Stats */}
      <div className="px-4 py-2 flex-shrink-0">
        <div className="grid grid-cols-3 gap-3">
          <div className="glass-card-sm p-3 text-center">
            <Navigation className="w-5 h-5 mx-auto mb-2 text-[#1d3557]" />
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {convertDistance(distance).toFixed(2)}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{distanceUnit}</span>
          </div>
          <div className="glass-card-sm py-[7px] px-4 text-center">
            <Clock className="w-5 h-5 mx-auto mb-2 text-[#3B82F6]" />
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatTime(elapsedTime)}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('walk.time')}</span>
          </div>
          <div className="glass-card-sm py-[7px] px-4 text-center">
            <Flame className="w-5 h-5 mx-auto mb-2 text-[#F59E0B]" />
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {calories}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>kcal</span>
          </div>
        </div>
      </div>

      {/* WalkMiniPlayer rimosso: senza una sorgente audio interna
          attiva non c'è nulla da mostrare in UI. La configurazione
          AVAudioSession `.ambient + mixWithOthers` in AppDelegate.swift
          continua a garantire che qualsiasi musica esterna (Apple
          Music / Spotify / podcast) non venga interrotta dall'app.
          Il componente WalkMiniPlayer resta in /components per uso
          futuro quando spedibile audio interno (coach vocale,
          playlist motivazionali). */}

      {/* Challenge peers — always visible (idle or tracking); active first, then inactive placeholders */}
      {(status === 'idle' || status === 'tracking' || status === 'paused') && activePeers.length > 0 && (() => {
        const activeCount = activePeers.filter(p => p.is_active).length;
        const isTracking = status === 'tracking' || status === 'paused';
        return (
        <div className="px-4 pb-2 flex-shrink-0" data-testid="peers-section">
          <div className="flex items-center gap-2 mb-2">
            <Users className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
            <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('walk.challengeMembers', 'Membri sfide')} · {activePeers.length}
              {activeCount > 0 && (
                <> · <span className="text-[#E8334A]">{activeCount}</span> {t('walk.liveNow', 'in live')}</>
              )}
            </span>
            {/* Walkie-talkie button moved into the action bar (bottom).
                This header now only holds informational counters. */}
            {activePeers.length > 3 && (
              <span className={`text-[10px] flex items-center gap-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t('walk.swipeToScroll', 'Scorri →')}
              </span>
            )}
          </div>
          <div className="relative">
            <div
              className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar"
              style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
              data-testid="peers-scroll-container"
            >
            {activePeers.map(peer => {
              const isActive = peer.is_active;
              const hasMedia = !!peer.has_live_media;
              const onCardClick = hasMedia ? () => openPeerLiveMedia(peer) : undefined;
              return (
                <div
                  key={peer.user_id}
                  onClick={onCardClick}
                  role={hasMedia ? 'button' : undefined}
                  tabIndex={hasMedia ? 0 : undefined}
                  onKeyDown={hasMedia ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPeerLiveMedia(peer); } } : undefined}
                  className={`relative flex-shrink-0 rounded-xl py-[7px] px-3 border transition-opacity ${hasMedia ? 'cursor-pointer' : ''} ${
                    hasMedia
                      ? 'border-2 border-pink-500 ring-2 ring-pink-500/40 ring-offset-2 ring-offset-transparent peer-live-pulse'
                      : isActive
                      ? (isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-sm')
                      : (isDark ? 'bg-white/[0.02] border-white/5 opacity-60' : 'bg-gray-50 border-gray-200 opacity-70')
                  } ${hasMedia && isDark ? 'bg-white/5' : ''} ${hasMedia && !isDark ? 'bg-white shadow-sm' : ''}`}
                  style={{ width: 'calc((100% - 16px) / 3)', minWidth: '110px', scrollSnapAlign: 'start' }}
                  data-testid={`${isActive ? 'active' : 'inactive'}-peer-${peer.user_id}`}
                >
                  {/* Live media badge — positioned INSIDE the card so
                      it's never clipped by the horizontal scroll
                      container. */}
                  {hasMedia && (
                    <span
                      className="absolute top-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-pink-500 shadow-md"
                      data-testid={`peer-live-media-badge-${peer.user_id}`}
                      aria-label={t('walk.liveMediaBadge', 'Contenuto live disponibile')}
                    >
                      {peer.live_media_types?.includes('video') ? (
                        <VideoIcon className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <Camera className="w-2.5 h-2.5 text-white" />
                      )}
                    </span>
                  )}
                  <div className="flex justify-center mb-2 relative">
                    {peer.user_picture ? (
                      <img
                        src={peer.user_picture}
                        alt={peer.user_name}
                        className={`w-10 h-10 rounded-full object-cover border-2 ${
                          isActive ? 'border-[#E8334A]' : (isDark ? 'border-white/10' : 'border-gray-300')
                        } ${!isActive ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 ${
                        isActive
                          ? 'bg-[#E8334A] border-[#E8334A]'
                          : (isDark ? 'bg-white/10 border-white/10 text-white/40' : 'bg-gray-200 border-gray-300 text-gray-400')
                      }`}>
                        {(peer.user_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isActive && (
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-white animate-pulse" aria-hidden="true" />
                    )}
                  </div>
                  {/* Nickname */}
                  <p className={`text-xs font-semibold text-center truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {peer.user_name || '—'}
                  </p>
                  {/* Distance */}
                  <p className={`text-center font-display text-sm ${
                    isActive ? 'text-[#E8334A]' : (isDark ? 'text-white/30' : 'text-gray-400')
                  }`}>
                    {isActive ? `${(peer.distance_km || 0).toFixed(2)} km` : '—'}
                  </p>
                  {/* Time / status */}
                  <p className={`text-[10px] text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {isActive
                      ? formatTime(peer.elapsed_sec || 0)
                      : t('walk.offline', 'non attivo')}
                  </p>
                </div>
              );
            })}
            </div>
            {/* Fade indicator on the right edge — hints at more cards when overflow */}
            {activePeers.length > 3 && (
              <div
                className="pointer-events-none absolute top-0 right-0 h-full w-8 rounded-r-xl"
                style={{
                  background: isDark
                    ? 'linear-gradient(to left, rgba(15,23,42,0.85), transparent)'
                    : 'linear-gradient(to left, rgba(249,250,251,0.95), transparent)',
                }}
              />
            )}
          </div>
        </div>
        );
      })()}

      {error && (
        <div className="px-4 mb-4">
          <p className="text-[#E8334A] text-sm text-center">{error}</p>
        </div>
      )}

      {/* Controls */}
      <div className="px-4 pb-4 flex-shrink-0">
        {status === 'idle' && (
          <div className="flex gap-2">
            <button
              onClick={requestStartTracking}
              className="btn-primary flex-1 py-4 flex items-center justify-center gap-2"
              data-testid="start-walk-tracking"
            >
              <Play className="w-5 h-5" />
              {t('walk.start')}
            </button>
          </div>
        )}

        {status === 'tracking' && (
          <div className="space-y-2">
            {/* Hold hint badges — visualized ABOVE the action buttons so
                they don't get hidden by other overlays e per dare
                feedback immediato vicino al tap. */}
            {showPauseHoldHint && (
              <div className={`text-center py-2 px-4 rounded-xl text-sm font-medium animate-fade-in ${
                isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'
              }`}>
                {t('walk.holdToPause')}
              </div>
            )}
            {showHoldHint && (
              <div className={`text-center py-2 px-4 rounded-xl text-sm font-medium animate-fade-in ${
                isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'
              }`}>
                {t('walk.holdToStop')}
              </div>
            )}
            <div className="flex gap-3">
              {/* Pause Button with Hold Progress (flex 2 of 2+2+1 = 5) */}
              <div className="relative" style={{ flex: 2 }}>
                <button 
                  onMouseDown={startPauseHold}
                  onMouseUp={cancelPauseHold}
                  onMouseLeave={cancelPauseHold}
                  onTouchStart={startPauseHold}
                  onTouchEnd={cancelPauseHold}
                  className={`w-full py-4 rounded-2xl flex items-center justify-center gap-2 font-semibold select-none overflow-hidden relative ${
                    isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                  style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
                  data-testid="pause-walk-tracking"
                >
                  {/* Pause Hold Progress Bar */}
                  {isPauseHolding && (
                    <div 
                      className="absolute inset-0 bg-orange-500/30 transition-all duration-75"
                      style={{ width: `${pauseHoldProgress}%` }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    <Pause className="w-5 h-5" />
                    {isPauseHolding ? pauseHoldCountdown : t('walk.pause')}
                  </span>
                </button>
              </div>
              {/* Stop Button with Hold Progress (flex 2) */}
              <div className="relative" style={{ flex: 2 }}>
                <button 
                  onMouseDown={startHold}
                  onMouseUp={cancelHold}
                  onMouseLeave={cancelHold}
                  onTouchStart={startHold}
                  onTouchEnd={cancelHold}
                  className="w-full py-4 rounded-2xl flex items-center justify-center gap-2 font-semibold bg-[#E8334A] text-white select-none overflow-hidden relative"
                  style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
                  data-testid="stop-walk-tracking"
                >
                  {/* Stop Hold Progress Bar */}
                  {isHolding && (
                    <div 
                      className="absolute inset-0 bg-red-700/50 transition-all duration-75"
                      style={{ width: `${holdProgress}%` }}
                    />
                  )}
                  <span className="relative z-10 flex items-center gap-2">
                    <Square className="w-5 h-5" />
                    {isHolding ? holdCountdown : t('walk.stop')}
                  </span>
                </button>
              </div>
              {/* Live Media — share photo/short video with active challenge peers */}
              <div className="relative" style={{ flex: 1 }}>
                <button
                  onClick={() => { setLiveMediaError(''); setLiveMediaOptionsOpen(true); }}
                  disabled={!activePeers.some(p => p.is_active)}
                  data-testid="live-media-share-btn"
                  title={activePeers.some(p => p.is_active)
                    ? (t('walk.liveMediaShare', 'Condividi foto / video') + '')
                    : (t('walk.walkieTalkieNoActive', 'Nessun membro attivo') + '')}
                  className={`w-full h-full py-4 rounded-2xl flex items-center justify-center font-semibold select-none ${
                    !activePeers.some(p => p.is_active)
                      ? (isDark ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : 'bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-md shadow-pink-500/30 active:scale-95 transition-transform'
                  }`}
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
              {/* Walkie-talkie REC Button — press-and-hold (WhatsApp-style).
                  Same height as the two buttons at its left thanks to h-full.
                  While held, shows a red progress bar that fills over 10s; at
                  10s the recording auto-stops and sends (same as release).
                  Hidden globally by admin toggle for non-admin users. */}
              {walkieAllowed && (
              <div className="relative" style={{ flex: 1 }}>
                <button
                  ref={walkieBtnRef}
                  aria-disabled={!activePeers.some(p => p.is_active)}
                  data-testid="walkie-talkie-btn"
                  title={activePeers.some(p => p.is_active)
                    ? (t('walk.walkieTalkieSend', 'Tieni premuto per registrare') + '')
                    : (t('walk.walkieTalkieNoActive', 'Nessun membro attivo') + '')}
                  style={{
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'none',
                    overscrollBehavior: 'contain',
                    transition: 'none',
                  }}
                  className={`w-full h-full py-4 rounded-2xl flex items-center justify-center font-semibold select-none overflow-hidden relative ${
                    !activePeers.some(p => p.is_active)
                      ? (isDark ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : 'bg-[#E8334A] text-white shadow-md shadow-red-500/30'
                  }`}
                >
                  {/* Central hold overlay (big red circle + REC symbol) is
                      rendered at the page root — the button itself stays
                      clean with just the mic icon.
                      `pointer-events: none` on the child span prevents
                      Android Chromium from treating a touch on the icon
                      as a "child element click" and firing premature
                      pointerup on the parent button. */}
                  <span
                    className="relative z-10 flex items-center justify-center"
                    style={{ pointerEvents: 'none' }}
                  >
                    <Mic className="w-5 h-5" style={{ pointerEvents: 'none' }} />
                  </span>
                </button>
              </div>
              )}
            </div>
          </div>
        )}

        {status === 'paused' && (
          <div className="space-y-2">
            {/* Hold hint badge sopra i bottoni — coerente con stato tracking */}
            {showHoldHint && (
              <div className={`text-center py-2 px-4 rounded-xl text-sm font-medium animate-fade-in ${
                isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'
              }`}>
                {t('walk.holdToStop')}
              </div>
            )}
            <div className="flex gap-3">
              <button 
                onClick={resumeTracking}
                className="py-4 rounded-2xl flex items-center justify-center gap-2 font-semibold bg-[#E8334A] text-white"
                style={{ flex: 2 }}
              >
                <Play className="w-5 h-5" />
                {t('walk.resume')}
              </button>
              <button 
                onMouseDown={startHold}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onTouchStart={startHold}
                onTouchEnd={cancelHold}
                className={`py-4 rounded-2xl flex items-center justify-center gap-2 font-semibold select-none ${
                  isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                }`}
                style={{ WebkitUserSelect: 'none', userSelect: 'none', flex: 2 }}
              >
                <Square className="w-5 h-5" />
                {t('walk.stop')}
              </button>
              {/* Walkie-talkie REC Button — press-and-hold (paused variant).
                  Hidden globally by admin toggle for non-admin users. */}
              {walkieAllowed && (
              <button
                ref={walkieBtnPausedRef}
                aria-disabled={!activePeers.some(p => p.is_active)}
                data-testid="walkie-talkie-btn-paused"
                title={activePeers.some(p => p.is_active)
                  ? (t('walk.walkieTalkieSend', 'Tieni premuto per registrare') + '')
                  : (t('walk.walkieTalkieNoActive', 'Nessun membro attivo') + '')}
                style={{
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'none',
                  overscrollBehavior: 'contain',
                  transition: 'none',
                  flex: 1,
                }}
                className={`h-full py-4 rounded-2xl flex items-center justify-center font-semibold select-none overflow-hidden relative ${
                  !activePeers.some(p => p.is_active)
                    ? (isDark ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                    : 'bg-[#E8334A] text-white shadow-md shadow-red-500/30'
                }`}
              >
                {/* Central hold overlay handles the visual feedback. */}
                <span
                  className="relative z-10 flex items-center justify-center"
                  style={{ pointerEvents: 'none' }}
                >
                  <Mic className="w-5 h-5" style={{ pointerEvents: 'none' }} />
                </span>
              </button>
              )}
            </div>
          </div>
        )}

        {status === 'finished' && !showPublishModal && (
          <div className="glass-card p-4 text-center">
            <Check className="w-12 h-12 text-[#16A34A] mx-auto mb-3" />
            <h3 className={`font-display text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('walk.completed')}
            </h3>
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {convertDistance(distance).toFixed(2)} {distanceUnit} {t('walk.in')} {formatTime(elapsedTime)}
            </p>
          </div>
        )}
      </div>
      </div>

      {/* Publish Modal - HIGH z-index to appear above map */}
      {showPublishModal && (
        <PublishWalkModal 
          distance={distance}
          duration={elapsedTime}
          calories={calories}
          isDark={isDark}
          onClose={() => {
            setShowPublishModal(false);
            navigate('/app/dashboard');
          }}
          onPublish={handlePublish}
          onSaveOnly={handleSaveOnly}
          publishing={publishing}
        />
      )}

      {/* Walk Ad Banner - shown when starting walk */}
      {showWalkAd && (
        <WalkAdBanner 
          duration={adConfig.walk_banner_duration || 5} 
          onClose={() => setShowWalkAd(false)} 
        />
      )}

      {/* Countdown Overlay for STOP - centered on screen */}
      {isHolding && (
        <div className="fixed inset-0 flex items-center justify-center z-[9998] pointer-events-none">
          <div className="relative w-32 h-32">
            {/* Background circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="rgba(0,0,0,0.7)"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="6"
              />
              {/* Progress circle that depletes */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#E8334A"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (holdProgress / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.05s linear' }}
              />
            </svg>
            {/* Countdown number */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-5xl font-bold text-white tabular-nums drop-shadow-lg">
                {holdCountdown}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Countdown Overlay for PAUSE - centered on screen */}
      {isPauseHolding && (
        <div className="fixed inset-0 flex items-center justify-center z-[9998] pointer-events-none">
          <div className="relative w-32 h-32">
            {/* Background circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="rgba(0,0,0,0.7)"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="6"
              />
              {/* Progress circle that depletes - orange for pause */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#F59E0B"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (pauseHoldProgress / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.05s linear' }}
              />
            </svg>
            {/* Countdown number */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-5xl font-bold text-white tabular-nums drop-shadow-lg">
                {pauseHoldCountdown}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Countdown Overlay for WALKIE-TALKIE REC — centered on screen, same
          layout as Stop/Pause but with a pulsing REC symbol instead of a
          number. Progress depletes over the 10-second recording window.
          ALWAYS rendered (visibility toggled via CSS) so the DOM doesn't
          change shape mid-press — Chromium WebView fires `touchcancel`
          when fixed-position elements are added during a touch sequence. */}
      <div
        className="fixed inset-0 flex items-center justify-center z-[9998] pointer-events-none"
        style={{ visibility: walkieHoldMode ? 'visible' : 'hidden' }}
      >
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">              <circle
                cx="50"
                cy="50"
                r="45"
                fill="rgba(0,0,0,0.7)"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="6"
              />
              {/* Progress circle — depletes from full to zero over 10 seconds.
                  `walkieHoldProgress` goes 0→100 via RAF started at press-down,
                  so the circle is ALWAYS full on the first paint (no lag for
                  the mic-permission prompt). No CSS transition — the RAF
                  frame rate is the animation itself. */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#E8334A"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (walkieHoldProgress / 100)}`}
              />
            </svg>
            {/* REC symbol — classic filled red disc that pulses while recording,
                with the mic icon in the center to reinforce "voice recording". */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="flex items-center justify-center w-12 h-12 rounded-full bg-[#E8334A] shadow-[0_0_20px_rgba(232,51,74,0.8)] animate-pulse"
                aria-label="Recording"
              >
                <Mic className="w-6 h-6 text-white" />
              </span>
            </div>
          </div>
        </div>

      {/* Hidden <audio> element primed at Start — iOS autoplay unlock */}
      <audio
        ref={walkieAudioElRef}
        playsInline
        preload="auto"
        style={{ display: 'none' }}
        data-testid="walkie-audio-el"
      />

      {/* Incoming walkie-talkie indicator — subtle toast at top.
          Hidden when the feature is disabled for this user. */}
      {walkieIncoming && walkieAllowed && (
        <div className="fixed top-16 inset-x-0 z-50 flex justify-center pointer-events-none">
          <div className="bg-[#E8334A] text-white rounded-full px-4 py-2 shadow-xl flex items-center gap-2 text-sm animate-pulse"
               data-testid="walkie-incoming-toast">
            {walkieIncoming.from_user_picture ? (
              <img src={walkieIncoming.from_user_picture} alt="" className="w-6 h-6 rounded-full object-cover border-2 border-white" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center text-[10px] font-bold">
                {(walkieIncoming.from_user_name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <Mic className="w-3.5 h-3.5" />
            <span className="font-medium truncate max-w-[220px]">
              {walkieIncoming.from_user_name || t('walk.walkieTalkieAnon', 'Qualcuno')}
            </span>
          </div>
        </div>
      )}

      {/* Walkie-talkie recording modal */}
      {walkieModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={walkieSending ? undefined : handleWalkieCancel}
          data-testid="walkie-modal"
        >
          <div
            className={`rounded-2xl w-full max-w-sm p-6 text-center ${isDark ? 'bg-gray-900 border border-white/10' : 'bg-white shadow-2xl'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('walk.walkieTalkieTitle', 'Walkie-talkie')}
              </h3>
              {!walkieSending && (
                <button onClick={handleWalkieCancel} className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {walkieError ? (
              <div className="py-6 text-red-500 text-sm">{walkieError}</div>
            ) : walkieSending ? (
              <div className="py-8">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#E8334A] border-t-transparent" />
                <p className={`mt-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('walk.walkieTalkieSending', 'Invio in corso…')}
                </p>
              </div>
            ) : (
              <>
                {/* Pulsing mic disc with countdown */}
                <div className="relative w-28 h-28 mx-auto mb-3">
                  <div className={`absolute inset-0 rounded-full ${walkieRecording ? 'bg-[#E8334A] animate-pulse' : 'bg-gray-400'}`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Mic className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-full px-3 py-0.5 text-sm font-bold text-[#E8334A] shadow">
                    {walkieCountdown}s
                  </div>
                </div>
                <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('walk.walkieTalkieRecording', 'Sto registrando… parla ora')}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('walk.walkieTalkieDelivery', 'L\'audio verrà inviato a tutti i compagni attivi')}
                </p>

                {/* Send-now button — active after the first second of audio */}
                <button
                  onClick={handleWalkieSendNow}
                  disabled={!walkieRecording || walkieCountdown > 9}
                  data-testid="walkie-send-now-btn"
                  className={`mt-5 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    (!walkieRecording || walkieCountdown > 9)
                      ? (isDark ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : 'bg-[#E8334A] text-white hover:bg-[#c02a3d] active:scale-[0.98] shadow-md shadow-red-500/30'
                  }`}
                >
                  {t('walk.walkieTalkieSendNow', 'Invia ora')}
                  {walkieRecording && walkieCountdown <= 9 && (
                    <span className="ml-2 opacity-80">
                      ({10 - walkieCountdown}s)
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Live Media: hidden file inputs for camera/gallery ─── */}
      <input
        ref={liveMediaPhotoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleLiveMediaFile(f, 'photo'); }}
        data-testid="live-media-photo-input"
      />
      <input
        ref={liveMediaVideoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleLiveMediaFile(f, 'video'); }}
        data-testid="live-media-video-input"
      />

      {/* ─── Live Media: source picker bottom sheet ─── */}
      {liveMediaOptionsOpen && (
        <div
          className="fixed inset-0 z-[9990] flex items-end justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setLiveMediaOptionsOpen(false)}
          data-testid="live-media-options-overlay"
        >
          <div
            className={`w-full max-w-md rounded-t-3xl p-5 pb-8 animate-slide-up ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="live-media-options-sheet"
          >
            <div className={`w-12 h-1.5 rounded-full mx-auto mb-4 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            <h3 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('walk.liveMediaTitle', 'Condividi un momento live')}
            </h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('walk.liveMediaSubtitle', 'Visibile per 30 minuti agli amici delle tue sfide attivi ora.')}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => liveMediaPhotoInputRef.current?.click()}
                data-testid="live-media-pick-photo"
                className="flex flex-col items-center justify-center py-6 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 text-white"
              >
                <ImageIcon className="w-8 h-8 mb-1.5" />
                <span className="font-semibold">{t('walk.liveMediaPhoto', 'Foto')}</span>
              </button>
              <button
                onClick={() => liveMediaVideoInputRef.current?.click()}
                data-testid="live-media-pick-video"
                className="flex flex-col items-center justify-center py-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white"
              >
                <VideoIcon className="w-8 h-8 mb-1.5" />
                <span className="font-semibold">{t('walk.liveMediaVideo', 'Video 10s')}</span>
              </button>
            </div>
            {liveMediaError && (
              <div className="mt-3 text-sm text-red-500 text-center" data-testid="live-media-error">{liveMediaError}</div>
            )}
            <button
              onClick={() => setLiveMediaOptionsOpen(false)}
              className={`mt-4 w-full py-3 rounded-full font-semibold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              data-testid="live-media-options-close"
            >
              {t('common.cancel', 'Annulla')}
            </button>
          </div>
        </div>
      )}

      {/* ─── Live Media: uploading overlay ─── */}
      {liveMediaUploading && (
        <div className="fixed inset-0 z-[9995] flex items-center justify-center bg-black/80 animate-fade-in" data-testid="live-media-uploading">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-white font-semibold">{t('walk.liveMediaUploading', 'Invio in corso...')}</p>
          </div>
        </div>
      )}

      {/* ─── Live Media: fullscreen viewer ─── */}
      {liveMediaViewer && (() => {
        const item = liveMediaViewer.items[liveMediaViewer.index];
        return (
          <div
            className="fixed inset-0 z-[10000] bg-black animate-fade-in"
            data-testid="live-media-viewer"
          >
            {/* Header overlay */}
            <div
              className="absolute top-0 left-0 right-0 z-10 px-4 pt-3 pb-6 flex items-center gap-3 text-white"
              style={{
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
              }}
            >
              {liveMediaViewer.ownerPicture ? (
                <img src={liveMediaViewer.ownerPicture} alt={liveMediaViewer.ownerName} className="w-9 h-9 rounded-full object-cover border-2 border-white" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold">
                  {(liveMediaViewer.ownerName || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{liveMediaViewer.ownerName}</div>
                <div className="text-xs text-white/70">
                  {liveMediaViewer.index + 1} / {liveMediaViewer.items.length}
                </div>
              </div>
            </div>

            {/* Tap-zone overlay (advance) */}
            <button
              type="button"
              onClick={handleViewerNext}
              className="absolute inset-0 w-full h-full"
              aria-label={t('walk.liveMediaNext', 'Successivo')}
              data-testid="live-media-viewer-tap-next"
              style={{ background: 'transparent' }}
            />

            {/* Media */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {(() => {
                if (!item) return null;
                // Normalize URL: if the backend returned a relative path
                // (e.g. /api/files/...) prefix it with REACT_APP_BACKEND_URL.
                const raw = item.media_url || '';
                const src = raw.startsWith('http') ? raw : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
                return item.media_type === 'video' ? (
                  <video
                    key={item.media_id}
                    src={src}
                    autoPlay
                    playsInline
                    controls={false}
                    onEnded={handleViewerNext}
                    className="max-w-full max-h-full pointer-events-auto"
                    data-testid="live-media-video"
                  />
                ) : (
                  <img
                    key={item.media_id}
                    src={src}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                    data-testid="live-media-photo"
                  />
                );
              })()}
            </div>

            {/* Close button — bottom right round */}
            <button
              onClick={handleViewerClose}
              className="absolute z-20 w-14 h-14 rounded-full bg-white text-gray-900 shadow-2xl flex items-center justify-center active:scale-95 transition-transform"
              style={{
                bottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 1rem))',
                right: '1.5rem',
              }}
              aria-label={t('common.close', 'Chiudi')}
              data-testid="live-media-viewer-close"
            >
              <X className="w-7 h-7" />
            </button>
          </div>
        );
      })()}

      {/* One-time informational modal (Android): location is used only
          during an active workout via the foreground location service. */}
      <LocationInfoModal
        open={locationInfoOpen}
        isDark={isDark}
        onAccept={handleLocationInfoAccept}
        onDecline={handleLocationInfoDecline}
      />
    </div>
  );
}

function PublishWalkModal({ distance, duration, calories, isDark, onClose, onPublish, onSaveOnly, publishing }) {
  const { t } = useTranslation();
  const { convertDistance, distanceUnit } = useUnits();
  const [text, setText] = useState('');
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showShareChoice, setShowShareChoice] = useState(false);

  // Block body scroll and hide bottom nav when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open-hide-nav');
    
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open-hide-nav');
    };
  }, []);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')} h`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    document.body.style.overflow = '';
    onClose();
  };

  const handlePublish = (e) => {
    // Open the scope-picker modal instead of publishing straight away.
    // The actual publish call runs from handleShareConfirm below.
    setShowShareChoice(true);
  };

  const handleShareConfirm = (challengeIds) => {
    document.body.style.overflow = '';
    setShowShareChoice(false);
    onPublish(text, challengeIds);
  };

  const handleSaveOnly = () => {
    document.body.style.overflow = '';
    onSaveOnly();
  };

  // Distinguish a walk from a run based on the average pace so the
  // modal title and placeholder match what the user actually did.
  const activityType = inferActivityType(distance, duration);
  const isRun = activityType === 'run';
  const titleText = isRun
    ? t('walk.saveRun', 'Salva la corsa')
    : t('walk.saveWalk');
  const commentPlaceholderText = isRun
    ? t('walk.commentPlaceholderRun', "Com'è andata la corsa?")
    : t('walk.commentPlaceholder');

  return (
    <div 
      className="fixed inset-0 flex items-end justify-center bg-black/70 backdrop-blur-sm overflow-hidden"
      style={{ zIndex: 99999 }}
    >
      <div 
        className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${
          isDark ? 'bg-[#0D1424]' : 'bg-white'
        }`}
        style={{ paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className={`w-12 h-1 rounded-full mx-auto mb-4 ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
        
        <h2 className={`font-display text-xl mb-5 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {titleText}
        </h2>

        {/* Summary - bigger cards */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="glass-card-sm p-4 text-center">
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {convertDistance(distance).toFixed(2)}
            </span>
            <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{distanceUnit}</span>
          </div>
          <div className="glass-card-sm p-4 text-center">
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatTime(duration)}
            </span>
            <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t('walk.time')}</span>
          </div>
          <div className="glass-card-sm p-4 text-center">
            <span className={`font-display text-2xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {calories}
            </span>
            <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>kcal</span>
          </div>
        </div>

        {/* Bigger textarea */}
        <div className="mb-5">
          <label className={`text-sm mb-2 block ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('walk.addComment')}
          </label>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={commentPlaceholderText}
            rows={4}
            className="input-glass resize-none"
          />
        </div>

        {/* Bigger buttons */}
        <div className="space-y-3">
          <div className="flex gap-3">
            <button 
              onClick={handleSaveOnly}
              disabled={publishing}
              className={`flex-1 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 ${
                isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <Save className="w-5 h-5" />
              {t('common.save')}
            </button>
            <button 
              onClick={handlePublish}
              disabled={publishing}
              className="flex-1 py-4 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 bg-[#E8334A] text-white"
            >
              <Share2 className="w-5 h-5" />
              {publishing ? t('walk.saving') : t('common.share')}
            </button>
          </div>
          <button 
            onClick={() => setShowConfirmCancel(true)} 
            className={`w-full py-3 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}
          >
            {t('walk.cancelWithoutSave')}
          </button>
        </div>
      </div>

      {/* Custom Confirm Cancel Modal */}
      {showConfirmCancel && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          style={{ zIndex: 100000 }}
        >
          <div className={`mx-4 w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'} shadow-2xl`}>
            <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('walk.confirmCancel.title')}
            </h3>
            <p className={`text-sm mb-6 text-center ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('walk.confirmCancel.message')}
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowConfirmCancel(false)}
                className={`flex-1 py-3 rounded-xl text-base font-semibold ${
                  isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t('walk.confirmCancel.back')}
              </button>
              <button 
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl text-base font-semibold bg-[#E8334A] text-white"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share scope picker — shown when user taps "Condividi" */}
      <SharePostModal
        open={showShareChoice}
        onClose={() => setShowShareChoice(false)}
        onConfirm={handleShareConfirm}
        publishing={publishing}
      />
    </div>
  );
}
